import React, { useState, useEffect, useRef, useCallback } from 'react';
import useElementHeight from '../hooks/useElementHeight';
import ReasonPromptModal from '../components/ReasonPromptModal';
import { toast } from 'sonner';
import { truncateName } from '../utils/previewTruncate';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { shouldRenderDesktop } from '../utils/deviceDetection';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import axios from 'axios';
import MediaPicker from '../components/MediaPicker';
import MarkSolvedNoteModal from '../components/MarkSolvedNoteModal';
import SavedTextsPanel from '../components/SavedTextsPanel';
import SavedPhotosPanel from '../components/SavedPhotosPanel';
import ZoomableImage from '../components/ZoomableImage';
import ActionStickerBubble from '../components/ActionStickerBubble';
import ThemeToggleMenuItem from '../components/ThemeToggleMenuItem';
import ChatHeaderActions from '../components/ChatHeaderActions';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { idbSaveMessages, idbGetMessages, idbSaveRoom, idbGetRoom, idbDeleteMessage, idbMarkDeleted, idbCleanupOld } from '../utils/idbCache';
import EditEntryModal from '../components/EditEntryModal';
import FlagIcons from '../components/FlagIcons';
import VoiceMessage from '../components/VoiceMessage';
import ReadReceipts from '../components/ReadReceipts';
import TypingIndicator from '../components/TypingIndicator';
import OnlineStatus from '../components/OnlineStatus';
import { onVisibilityFetch } from '../utils/pollingKeepAlive';
import { takePrefetch } from '../utils/messagePrefetch';
import WhatsAppInput from '../components/WhatsAppInput';
import AudioCallScreen from '../components/AudioCallScreen';
import EditClientModal from '../components/EditClientModal';
import MBSetupBadge from '../components/MBSetupBadge';
import { useMBList } from '../hooks/useMBList';
import AssignMBQuickPicker from '../components/AssignMBQuickPicker';
import MultiSelectForwardModal from '../components/MultiSelectForwardModal';
import {
  connectSocket,
  disconnectSocket,
  joinRoom,
  leaveRoom,
  sendMessage as sendSocketMessage,
  sendTypingIndicator,
  markMessagesRead,
  onNewMessage,
  onUserTyping,
  onUserOnline,
  onUserOffline,
  onMessagesRead,
  onMessagesDelivered,
  offNewMessage,
  offUserTyping,
  offUserOnline,
  offUserOffline,
  offMessagesRead,
  offMessagesDelivered,
  onMessageUpdated,
  offMessageUpdated,
  onMessageDeleted,
  offMessageDeleted} from '../utils/socket';
import socket from '../utils/socket';
import { deltaSyncMessages } from '../utils/messageSync';
import { parseBankFromPaste } from '../utils/parseBankFromPaste';
import DraggableModal from '../components/DraggableModal';
import { 
  ArrowLeft,
  UserCircle,
  PaperPlaneRight,
  DotsThreeVertical,
  Trash,
  Copy,
  ShareFat,
  Smiley,
  Plus,
  Prohibit,
  ArrowBendUpLeft,
  X,
  FileText,
  CaretDown,
  Phone,
  WhatsappLogo,
  Star,
  ArrowsLeftRight,
  Check,
  Checks,
  Clock,
  WarningCircle,
  PushPin,
  Bell,
  BellSlash,
  Eye,
} from '@phosphor-icons/react';
import PinDurationPicker from '../components/PinDurationPicker';
import ViewOnceBubble from '../components/ViewOnceBubble';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExts = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
  const lower = url.split('?')[0].toLowerCase();
  return videoExts.some(ext => lower.endsWith(ext));
};

const buildMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  return `${API}${url}`;
};

// Date separator helpers
const getDateLabel = (timestamp) => {
  if (!timestamp) return '';
  const msgDate = new Date(timestamp);
  if (isNaN(msgDate.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (msgDate.toDateString() === today.toDateString()) return 'Today';
  if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return msgDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' });
};

const shouldShowDateSeparator = (msgs, idx) => {
  if (idx === 0) return true;
  const prev = new Date(msgs[idx - 1].timestamp);
  const curr = new Date(msgs[idx].timestamp);
  if (isNaN(prev.getTime()) || isNaN(curr.getTime())) return false;
  return prev.toDateString() !== curr.toDateString();
};

// Session-53 hardening — also treat data-URI SVGs as stickers even if the
// `is_sticker` flag is missing (old messages / migration edge case). Every
// built-in sticker uses `data:image/svg+xml`, so this catches strays.
const isStickerLike = (m) => !!(m && (m.is_sticker || (typeof m.image_url === 'string' && m.image_url.startsWith('data:image/svg'))));

// Helper to detect consecutive image groups from the same sender
const getImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || isStickerLike(msg)) return null;
  if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return null;
  if (idx > 0) {
    const prev = msgs[idx - 1];
    if (prev.image_url && !prev.voice_url && !prev.document_url && !isVideoUrl(prev.image_url) && !isStickerLike(prev) && prev.sender_id === msg.sender_id && !prev.caption && (!prev.text || !prev.text.trim() || prev.text === '🖼️ Photo' || prev.text === '🎥 Video')) return null;
  }
  const group = [msg];
  for (let i = idx + 1; i < msgs.length; i++) {
    if (group.length >= 10) break;
    const nextMsg = msgs[i];
    // Bug #1b fix (Session-53) — added `!isStickerLike(nextMsg)` so a sticker
    // sent AFTER a photo doesn't get merged into the photo collage.
    if (!nextMsg.image_url || nextMsg.voice_url || nextMsg.document_url || isVideoUrl(nextMsg.image_url) || isStickerLike(nextMsg) || nextMsg.sender_id !== msg.sender_id) break;
    const hasCaption = nextMsg.caption || (nextMsg.text && nextMsg.text.trim() && nextMsg.text !== '🖼️ Photo' && nextMsg.text !== '🎥 Video');
    if (!hasCaption) {
      group.push(nextMsg);
    } else {
      // Point 6 — a captioned photo is standalone; stop WITHOUT adding it.
      break;
    }
  }
  return group.length > 1 ? group : null;
};

const isPartOfImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || isStickerLike(msg) || idx === 0) return false;
  // Point 6 — a captioned photo is always its own standalone message.
  if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return false;
  let groupCount = 0;
  for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
    const prevMsg = msgs[i];
    // Bug #1a fix (Session-53) — `!isStickerLike(prevMsg)` so a photo right
    // after a sticker isn't wrongly hidden.
    if (prevMsg.image_url && !prevMsg.voice_url && !prevMsg.document_url && !isVideoUrl(prevMsg.image_url) && !isStickerLike(prevMsg) && prevMsg.sender_id === msg.sender_id && !prevMsg.caption && (!prevMsg.text || !prevMsg.text.trim() || prevMsg.text === '🖼️ Photo' || prevMsg.text === '🎥 Video')) {
      groupCount++;
    } else break;
  }
  return groupCount > 0 && groupCount < 10;
};

const HandlerChat = ({ embeddedRoomId, onBackToList }) => {
  const params = useParams();
  const roomId = embeddedRoomId || params.roomId;
  const MB_SETUPS = useMBList();
  const { user } = useAuth();
  // Point 48 — Calling Handler reaches this screen for read-only chat
  // viewing only. Derived directly from the authenticated user's role (not
  // a URL param, which could be tampered with) — the backend also hard
  // 403s any send from this role, this just keeps the UI honest.
  const isReadOnlyViewer = user?.role === 'calling_handler';
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification, playNotificationSound, requestPermission } = useWebNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  // P5 — direct /handler-chat/:id navigation on desktop bounces to the
  // dashboard with the room pre-selected so it opens as the EMBEDDED chat
  // (keeps split-view). Routed instance only; mobile keeps full-screen.
  const _p5Redirected = useRef(false);
  useEffect(() => {
    if (embeddedRoomId || !params.roomId) return;
    if (_p5Redirected.current || !shouldRenderDesktop()) return;
    _p5Redirected.current = true;
    const st = location.state || {};
    navigate('/handler-dashboard', {
      replace: true,
      state: { selectedChatId: params.roomId, activeTab: st.activeTab, chatTypeTab: st.chatTypeTab },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedRoomId, params.roomId]);

  const [messages, setMessages] = useState([]);
  // Keep a live ref of messages so reconnect delta-sync can read the current
  // thread without stale closures.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [inputText, setInputText] = useState('');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false); // Changed to false for instant loading
  const [showProfile, setShowProfile] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  // Session-40: Service Request modals state
  const [showSRCreateModal, setShowSRCreateModal] = useState(false);
  // Session-48j — Mark-as-Solved optional note dialog state
  const [markSolvedOpen, setMarkSolvedOpen] = useState(false);
  const [markSolvedBusy, setMarkSolvedBusy] = useState(false);
  const [markSolvedReqId, setMarkSolvedReqId] = useState(null);
  const doMarkSolved = async (note) => {
    setMarkSolvedBusy(true);
    try {
      const res = await axios.post(`${API}/support/mark-solved`, { room_id: roomId, actor_id: user.id, note, request_id: markSolvedReqId });
      if (res.data && res.data.no_open_request) {
        toast.error(res.data.message || 'No pending request to approve.');
        setMarkSolvedOpen(false);
        return;
      }
      toast.success('✅ Marked as solved');
      setMarkSolvedOpen(false);
      // Session-49: refresh chat instantly so the bot's "Marked as SOLVED" ack
      // shows up without waiting on socket delivery / polling. On mobile the
      // socket occasionally misses the emit, forcing the user to leave-and-
      // reopen the chat to see the acknowledgement. Explicit refresh fixes it.
      try { loadMessagesRef.current && loadMessagesRef.current(); } catch (_) {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark solved');
    } finally { setMarkSolvedBusy(false); }
  };
  const [showSRCancelModal, setShowSRCancelModal] = useState(false);
  const [srCancelList, setSrCancelList] = useState([]);
  // Session-41: reason state for the 3-dot Cancel Request flow
  const [srReasonForId, setSrReasonForId] = useState(null);
  const [srReasonText, setSrReasonText] = useState('');
  const [srAmount, setSrAmount] = useState('');
  const [srReference, setSrReference] = useState('');
  const [srBusy, setSrBusy] = useState(false);
  // P23/P24 — combined create+approve fields
  const [srUtr, setSrUtr] = useState('');
  const [srReceivedIn, setSrReceivedIn] = useState('');
  const [srBank, setSrBank] = useState({ account_holder_name: '', bank_account_number: '', ifsc_code: '', bank_name: '', upi_id: '' });
  const [srBankAccounts, setSrBankAccounts] = useState([]); // P24 multi-account picker
  const [srBankPickId, setSrBankPickId] = useState('');

  // P24 — prefill customer bank details when opening Create Request on a withdraw
  // chat; expose a dropdown if the client has multiple saved accounts.
  useEffect(() => {
    if (!showSRCreateModal || customer?.chat_type !== 'withdrawal' || !customer?.id) return;
    let cancelled = false;
    (async () => {
      let base = { account_holder_name: '', bank_account_number: '', ifsc_code: '', bank_name: '', upi_id: '' };
      try {
        const res = await axios.get(`${API}/client-bank-details/${customer.id}`);
        const d = res.data?.bank_details || {};
        base = {
          account_holder_name: d.account_holder_name || '',
          bank_account_number: d.bank_account_number || '',
          ifsc_code: d.ifsc_code || '',
          bank_name: d.bank_name || '',
          upi_id: d.upi_id || '',
        };
      } catch (_) {}
      let accounts = [];
      try {
        const h = await axios.get(`${API}/clients/${customer.id}/field-history`, { params: { field: 'bank' } });
        accounts = (h.data?.entries || [])
          .map((e) => ({ id: e.id, value: e.value || {}, is_active: e.is_active }))
          .filter((a) => a.value.bank_account_number || a.value.account_holder_name);
      } catch (_) {}
      if (cancelled) return;
      setSrBankAccounts(accounts);
      const active = accounts.find((a) => a.is_active) || accounts[0];
      if (active) {
        const v = active.value;
        setSrBankPickId(active.id);
        setSrBank({
          account_holder_name: v.account_holder_name || base.account_holder_name,
          bank_account_number: v.bank_account_number || base.bank_account_number,
          ifsc_code: v.ifsc_code || base.ifsc_code,
          bank_name: v.bank_name || base.bank_name,
          upi_id: base.upi_id,
        });
      } else {
        setSrBankPickId('');
        setSrBank(base);
      }
    })();
    return () => { cancelled = true; };
  }, [showSRCreateModal, customer?.chat_type, customer?.id]);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showMBMenu, setShowMBMenu] = useState(false);
  const [longPressMenu, setLongPressMenu] = useState(null); // {messageId, x, y}
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [contextMoreOpen, setContextMoreOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // messageId
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [pastedFile, setPastedFile] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // { type: 'deposit'|'withdraw', entryId }

  // Point 50 — the keyboard must only open when the user explicitly taps
  // the message box, never automatically on opening a chat. If a search
  // box (or any other input) was focused on the previous screen, the
  // mobile keyboard can otherwise carry over into this page on mount.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && typeof active.blur === 'function') {
      active.blur();
    }
  }, []);

  // Feature 17: Global paste listener — image from clipboard opens MediaPicker with preview
  useEffect(() => {
    const onPaste = (e) => {
      // Point 2 fix — works even inside the message box. Inspect clipboard:
      // image item → intercept + preview; plain text → leave untouched.
      const items = e.clipboardData?.items || [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
          const blob = items[i].getAsFile();
          if (blob) {
            e.preventDefault();
            const file = new File([blob], `screenshot-${Date.now()}.png`, { type: blob.type || 'image/png' });
            setPastedFile(file);
            setShowMediaPicker(true);
          }
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);
  const [showSavedTextsPanel, setShowSavedTextsPanel] = useState(false);
  const [showSavedPhotosPanel, setShowSavedPhotosPanel] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  // Pagination state — infinite scroll for messages (WhatsApp-style)
  const [olderLoading, setOlderLoading] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({ name: '', phone: '', password: 'Abcd1234' });
  const [registering, setRegistering] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editClientData, setEditClientData] = useState(null);
  
  // Reply & Forward features
  const [replyingTo, setReplyingTo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [availableChats, setAvailableChats] = useState([]);
  const [messageToForward, setMessageToForward] = useState(null);
  
  // Phase 2: Advanced features
  const [customerTyping, setCustomerTyping] = useState(false);
  const [customerOnlineStatus, setCustomerOnlineStatus] = useState({ is_online: false, last_seen: null });
  // Offline-cache indicator — true when the browser reports a live network.
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    idbCleanupOld();
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const typingCheckRef = useRef(null);

  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [allMediaUrls, setAllMediaUrls] = useState([]);
  const [currentMediaIdx, setCurrentMediaIdx] = useState(0);

  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const menuRef = useRef(null);
  const longPressTimer = useRef(null);
  const [inputKey, setInputKey] = useState(0);
  const chatContainerRef = useRef(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [showForwardGroupModal, setShowForwardGroupModal] = useState(false);
  const [forwardGroupMsg, setForwardGroupMsg] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]); // point 43 — max 2
  const [pinDurationTarget, setPinDurationTarget] = useState(null);
  const [isChatMuted, setIsChatMuted] = useState(false); // point 52
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const containerRef = useRef(null);
  // Point-1 — measure the live chat-header height (profile row + actions strip)
  // so the message list / pinned banner offset by its ACTUAL height.
  const chatHeaderRef = useRef(null);
  const headerHeight = useElementHeight(chatHeaderRef, 96);
  
  // Visual Viewport Keyboard Fix (Android Chrome + iOS Safari)
  // Pin the container to the current visual viewport (both height AND top) so
  // the input bar stays right above the keyboard and can't be pulled up
  // leaving a white gap when the user scrolls or the visual viewport shifts.
  //
  // Session-32 (Feb-13-2026): on keyboard-open (viewport SHRINK) we also
  // auto-scroll the chat feed to the newest message — WhatsApp-style — so the
  // last bubble isn't hidden behind the input bar/keyboard the moment the
  // user taps the composer.
  useEffect(() => {
    const el = containerRef.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;
    let lastHeight = vv.height;
    const apply = () => {
      el.style.height = `${vv.height}px`;
      el.style.top = `${vv.offsetTop}px`;
      // Keyboard just opened → viewport height dropped by >100px. Push the
      // chat to the bottom so the latest message stays visible.
      if (vv.height < lastHeight - 100) {
        requestAnimationFrame(() => {
          try {
            const c = chatContainerRef.current;
            if (c) c.scrollTop = c.scrollHeight;
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: 'end' });
          } catch {}
        });
      }
      lastHeight = vv.height;
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      if (el) { el.style.height = ''; el.style.top = ''; }
    };
  }, []);

  // Listen for incoming calls
  useEffect(() => {
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user.id) {
        setIncomingCall({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerProfilePic: data.caller_profile_pic,
          isConference: data.is_conference || false,
          participantNames: data.participant_names || []
        });
      }
    };
    socket.on('incoming_call', handleIncomingCall);
    return () => socket.off('incoming_call', handleIncomingCall);
  }, [user]);
  
  // Track which messages have already played notification sound
  const notifiedMessageIds = useRef(new Set());
  
  // Ref to track previous messages count for notification logic
  const prevMessagesCountRef = useRef(0);
  // Flag to skip notification on initial load
  const isInitialLoadRef = useRef(true);

  const EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];

  // Register customer from chat
  const handleRegisterCustomer = async () => {
    if (!registerForm.name || !registerForm.phone) {
      toast.error('Name and mobile number are required');
      return;
    }
    if (registerForm.phone.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }
    
    setRegistering(true);
    try {
      await axios.post(`${API}/inquiry/register-customer`, {
        guest_user_id: customer?.id || '',
        name: registerForm.name,
        phone: registerForm.phone,
        password: registerForm.password || 'Abcd1234',
        room_id: roomId,
        registered_by: user.id
      });
      
      toast.success('Customer registered successfully!');
      setShowRegisterModal(false);
      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
      // Reload messages to show the auto-sent credentials
      loadMessages();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to register customer';
      toast.error(msg);
    }
    setRegistering(false);
  };



  // Point 43 — fetch this room's pinned messages (up to 2)
  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    (async () => {
      try {
        const roomRes = await axios.get(`${API}/chat/room/${roomId}`);
        const pins = roomRes.data?.pinned_messages || [];
        if (pins.length === 0) { if (!cancelled) setPinnedMessages([]); return; }
        const msgsRes = await axios.get(`${API}/chat/messages/${roomId}`);
        const pinIds = pins.map(p => p.message_id);
        const pinMsgs = pinIds.map(id => msgsRes.data.find(m => m.id === id)).filter(Boolean);
        if (!cancelled) setPinnedMessages(pinMsgs);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Point 52 — mute-chat status for this room.
  useEffect(() => {
    if (!roomId || !user?.id) return;
    let cancelled = false;
    axios.get(`${API}/chat/room/${roomId}/mute-status`, { params: { user_id: user.id } })
      .then(r => { if (!cancelled) setIsChatMuted(!!r.data?.is_muted); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [roomId, user?.id]);

  // Stable loadMessages function - no dependencies that change frequently
  // Fix doc #1 — keep cached history fresh as messages change (incl. socket updates).
  useEffect(() => {
    if (roomId && Array.isArray(messages) && messages.length) {
      writeInstantCache('room_messages', roomId, messages.slice(-50));
    }
  }, [messages, roomId]);

  const loadMessages = useCallback(async () => {
    if (!roomId || !user?.id) return;

    // Bug #2 fix (Session-53) — clear the messages array + flip loading ON
    // as the very first line so the previous room's chat doesn't flash
    // for a second while the new room's messages are fetching. Without
    // this, switching Deposit ↔ Withdraw ↔ Support panels showed the
    // old chat for ~1s until the axios response landed.
    setMessages([]);
    setLoading(true);

    try {
      // Session-38 perf: consume any prefetch primed by HandlerDashboard on
      // touchstart / mousedown. If fresh, we skip the network round-trip
      // entirely and use the already-in-flight promise.
      const prefetched = takePrefetch(roomId);
      // Fix doc #1 — instant paint from cached history on first open, then refresh below.
      try {
        const _cachedMsgs = readInstantCache('room_messages', roomId);
        if (_cachedMsgs && Array.isArray(_cachedMsgs)) {
          setMessages(prev => (prev && prev.length) ? prev : _cachedMsgs);
        }
      } catch (_) {}
      // Durable IndexedDB cache — instant paint even offline / after full app close.
      idbGetMessages(roomId).then(idbMsgs => {
        if (idbMsgs && idbMsgs.length) setMessages(prev => (prev && prev.length) ? prev : idbMsgs);
      }).catch(() => {});
      let responseData;
      if (prefetched) {
        try {
          responseData = await prefetched;
        } catch (_) { responseData = null; }
      }
      if (!responseData) {
        // Pagination: load latest 50 messages initially (older loaded on scroll-up)
        const response = await axios.get(`${API}/chat/messages/${roomId}`, {
          params: { user_id: user.id, limit: 50 }
        });
        responseData = response.data;
      }

      if (responseData && Array.isArray(responseData)) {
        idbSaveMessages(roomId, responseData);
        console.log(`✅ Handler loaded ${responseData.length} messages for room ${roomId}`);
        // If fewer than page size returned, we know there are no more older
        setHasMoreOlder((responseData.length || 0) >= 50);
        
        // Skip notifications on initial load
        if (isInitialLoadRef.current) {
          console.log('📥 Handler: Initial load - skipping notifications');
          isInitialLoadRef.current = false;
          prevMessagesCountRef.current = responseData.length;
          // Mark all existing messages as already notified
          responseData.forEach(msg => notifiedMessageIds.current.add(msg.id));
          setMessages(responseData);
          // Continue to mark messages as read below
        } else {
          // Check for new messages from customer and show notification
          const prevMessagesLength = prevMessagesCountRef.current;
          const newMessages = responseData;
        
        if (prevMessagesLength > 0 && newMessages.length > prevMessagesLength) {
          // Get only the NEW messages
          const newlyAddedMessages = newMessages.slice(prevMessagesLength);
          
          newlyAddedMessages.forEach((newMsg) => {
            // Notify if message is from customer AND we haven't notified for this message yet
            if (newMsg.sender_id !== user.id && 
                newMsg.sender_role === 'customer' &&
                !notifiedMessageIds.current.has(newMsg.id)) {
              
              console.log('🔔 Handler: New message from customer, showing notification for message:', newMsg.id);
              
              // Mark as notified
              notifiedMessageIds.current.add(newMsg.id);
              
              const messagePreview = newMsg.message_type === 'text' 
                ? newMsg.message_text 
                : newMsg.message_type === 'image' 
                ? '📷 Photo' 
                : newMsg.message_type === 'video'
                ? '🎥 Video'
                : newMsg.message_type === 'voice'
                ? '🎤 Voice message'
                : 'New message';
              
              showNotificationRef.current({
                title: `${customer?.name || 'Customer'} - DWS Vault Chat`,
                body: messagePreview,
                tag: `chat-${roomId}`,
                data: {
                  messageId: newMsg.id,
                  roomId: roomId,
                  senderId: newMsg.sender_id,
                  messageType: newMsg.message_type
                }
              });
            }
          });
        }
        
        // Update ref for next comparison
        prevMessagesCountRef.current = newMessages.length;
        // Auto-hide handoff_summary messages older than 24 hours
        const _filtered = responseData.filter((m) => {
          if (m?.bot_type !== 'handoff_summary') return true;
          try { return (Date.now() - new Date(m.timestamp).getTime()) / 3600000 < 24; } catch { return true; }
        });
        setMessages(_filtered);
        } // End of else block for non-initial load
        
        // Mark only RECEIVED messages as read (not own sent messages)
        const unreadMessageIds = responseData
          .filter(msg => !msg.read && msg.sender_id !== user.id)
          .map(msg => msg.id);
        
        if (unreadMessageIds.length > 0) {
          try {
            await axios.post(`${API}/messages/mark-read`, {
              message_ids: unreadMessageIds
            }, {
              params: { user_id: user.id }
            });
            console.log(`✅ Marked ${unreadMessageIds.length} messages as read`);
          } catch (error) {
            console.error('Failed to mark messages as read:', error);
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setLoading(false);
      // Don't clear existing messages on error
    }
  }, [roomId, user?.id, customer?.name]); // showNotification via ref

  // Load OLDER messages when user scrolls to the top (WhatsApp-style pagination)
  const loadOlderMessages = useCallback(async () => {
    if (!roomId || !user?.id) return;
    if (olderLoading || !hasMoreOlder) return;
    if (!messages || messages.length === 0) return;
    const oldest = messages[0]?.timestamp;
    if (!oldest) return;

    setOlderLoading(true);
    const container = chatContainerRef.current;
    const prevHeight = container?.scrollHeight || 0;
    const prevTop = container?.scrollTop || 0;

    try {
      const resp = await axios.get(`${API}/chat/messages/${roomId}`, {
        params: { user_id: user.id, before: oldest, limit: 50 }
      });
      if (resp.data && Array.isArray(resp.data)) {
        const older = resp.data;
        if (older.length === 0) {
          setHasMoreOlder(false);
        } else {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newOlder = older.filter(m => !existingIds.has(m.id));
            // Mark older as already notified so they don't trigger notification
            newOlder.forEach(m => notifiedMessageIds.current.add(m.id));
            return [...newOlder, ...prev];
          });
          setHasMoreOlder(older.length >= 50);
          // Preserve scroll position after DOM update
          requestAnimationFrame(() => {
            if (container) {
              const newHeight = container.scrollHeight;
              container.scrollTop = prevTop + (newHeight - prevHeight);
            }
          });
        }
      }
    } catch (e) {
      console.error('Failed to load older messages:', e);
    } finally {
      setOlderLoading(false);
    }
  }, [roomId, user?.id, olderLoading, hasMoreOlder, messages]);

  // Handle image preview
  const handleImageClick = (imageUrl) => {
    // Build list of all media URLs in chat
    const mediaList = messages
      .filter(m => m.image_url)
      .map(m => {
        if (m.image_url.startsWith('http')) return m.image_url;
        if (m.image_url.startsWith('/api/')) return `${BACKEND_URL}${m.image_url}`;
        if (m.image_url.startsWith('/uploads/')) return `${BACKEND_URL}/api${m.image_url}`;
        return `${API}${m.image_url}`;
      });
    setAllMediaUrls(mediaList);
    const idx = mediaList.indexOf(imageUrl);
    setCurrentMediaIdx(idx >= 0 ? idx : 0);
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Detect if user is scrolling up to read old messages + trigger load older near top
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is more than 100px from bottom, they're reading old messages
    setIsUserScrolling(distanceFromBottom > 100);
    
    // Near the top? Load older messages (WhatsApp-style infinite scroll)
    if (scrollTop < 120 && hasMoreOlder && !olderLoading) {
      loadOlderMessages();
    }
  };

  const prevMsgLenRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      if (prevMsgLenRef.current === 0) {
        scrollToBottom();
        requestAnimationFrame(scrollToBottom);
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 500);
      } else if (messages.length > prevMsgLenRef.current && !isUserScrolling) {
        requestAnimationFrame(scrollToBottom);
      }
      prevMsgLenRef.current = messages.length;
    }
  }, [messages.length, roomId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Store loadMessages in a ref to avoid dependency issues
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  const syncCtxRef = useRef({ roomId, userId: user?.id });
  useEffect(() => { syncCtxRef.current = { roomId, userId: user?.id }; });

  // Point 20 — on socket reconnect, silently catch up this chat's messages so
  // any message missed during the disconnect gap appears without reopening.
  useEffect(() => {
    const onReconnect = async () => {
      try {
        const { roomId: rid, userId } = syncCtxRef.current || {};
        const did = await deltaSyncMessages({
          roomId: rid, userId,
          getMessages: () => messagesRef.current,
          setMessages,
        });
        if (!did && loadMessagesRef.current) loadMessagesRef.current();
      } catch (_) {
        try { loadMessagesRef.current && loadMessagesRef.current(); } catch (_) {}
      }
    };
    window.addEventListener('socket:reconnected', onReconnect);
    return () => window.removeEventListener('socket:reconnected', onReconnect);
  }, []);

  // Load messages - initial load + visibility refresh (Socket.IO handles real-time)
  useEffect(() => {
    // Reset pagination state when switching rooms
    setHasMoreOlder(true);
    setOlderLoading(false);
    // Initial load
    loadMessagesRef.current();
    
    // Instant refresh when tab returns to focus
    const cleanupVisibility = onVisibilityFetch(() => {
      loadMessagesRef.current();
    });
    
    // Cleanup
    return () => {
      cleanupVisibility();
    };
  }, [roomId, user?.id]);

  // Socket.IO real-time messaging (alongside polling for zero-risk)
  useEffect(() => {
    if (!roomId || !user?.id) return;
    
    connectSocket(user.id);
    joinRoom(roomId, user.id);
    console.log('🔌 Socket.IO joined room:', roomId);
    
    const handleNewMessage = (data) => {
      if (data.room_id === roomId && data.message) {
        setMessages(prev => {
          // Bug #3 fix (Session-53) — unified dedupe. If the incoming
          // message already exists by server id OR by client_message_id
          // (either as an optimistic bubble waiting for confirmation OR
          // a real doc already inserted by the axios response), replace
          // it in-place instead of appending. This kills the "photo shows
          // twice" race between socket echo and HTTP response arrival.
          const cmid = data.message.client_message_id;
          const existingIdx = prev.findIndex(m =>
            m.id === data.message.id ||
            (cmid && m.client_message_id === cmid)
          );
          if (existingIdx !== -1) {
            const next = prev.slice();
            next[existingIdx] = { ...prev[existingIdx], ...data.message, pending: false, _uploading: false };
            return next;
          }
          return [...prev, data.message];
        });
        idbSaveMessages(roomId, [data.message]);
      }
    };
    
    // Listen for typing indicators via Socket.IO
    const handleUserTyping = (data) => {
      if (data.room_id === roomId && data.user_id !== user.id) {
        setCustomerTyping(data.is_typing);
      }
    };
    
    // Listen for read receipts (customer read our messages → update ticks)
    const handleMessagesRead = (data) => {
      if (data.room_id !== roomId) return;
      if (Array.isArray(data.message_ids) && data.message_ids.length) {
        const ids = new Set(data.message_ids);
        setMessages(prev => prev.map(m =>
          ids.has(m.id) ? { ...m, delivered: true, read: true } : m
        ));
      } else if (data.auto || data.read_all) {
        setMessages(prev => prev.map(m =>
          m.sender_id === user.id ? { ...m, delivered: true, read: true } : m
        ));
      }
    };
    
    // Listen for delivered receipts (customer received our messages → double tick)
    const handleMessagesDelivered = (data) => {
      if (data.room_id === roomId && data.message_ids) {
        setMessages(prev => prev.map(m => 
          data.message_ids.includes(m.id) ? { ...m, delivered: true } : m
        ));
      }
    };

    // Listen for message edits (deposit/withdraw admin edits + revert strikes)
    const handleMessageUpdated = (data) => {
      if (data.room_id === roomId && data.message_id) {
        setMessages(prev => prev.map(m =>
          m.id === data.message_id
            ? { ...m,
                text: data.text ?? m.text,
                meta: { ...(m.meta || {}), ...(data.meta || {}) },
                edited_at: data.edited_at || m.edited_at,
                text_striked: data.text_striked !== undefined ? data.text_striked : m.text_striked,
                revert_reason: data.revert_reason !== undefined ? data.revert_reason : m.revert_reason,
                reverted_at: data.reverted_at || m.reverted_at }
            : m
        ));
      }
    };
    
    onNewMessage(handleNewMessage);
    onUserTyping(handleUserTyping);
    onMessagesRead(handleMessagesRead);
    onMessagesDelivered(handleMessagesDelivered);
    onMessageUpdated(handleMessageUpdated);
    const handleMessageDeleted = ({ message_id, delete_for_everyone }) => {
      if (!delete_for_everyone) { idbDeleteMessage(message_id); return; }
      setMessages(prev => prev.map(m =>
        m.id === message_id ? { ...m, deleted_for_everyone: true, is_deleted_view: true } : m
      ));
      idbMarkDeleted(message_id);
    };
    onMessageDeleted(handleMessageDeleted);
    
    return () => {
      offNewMessage(handleNewMessage);
      offUserTyping(handleUserTyping);
      offMessagesRead(handleMessagesRead);
      offMessagesDelivered(handleMessagesDelivered);
      offMessageUpdated(handleMessageUpdated);
      offMessageDeleted(handleMessageDeleted);
      leaveRoom(roomId, user.id);
    };
  }, [roomId, user?.id]);

  // Load customer info. Point 4 — this header data (name, phone, DP, MB
  // setup, chat-type label) is display-only, never a live financial value,
  // so it paints instantly from THIS room's own last-known cache (never a
  // stale previous room's data, since the cache is keyed by roomId) while
  // the real request runs silently behind it — fixes the "delay opening a
  // chat" symptom while still avoiding the Session-53 cross-room flash bug
  // this effect used to guard against with an immediate setCustomer(null).
  useEffect(() => {
    if (!roomId) return;

    const cached = readInstantCache('room_customer', roomId);
    setCustomer(cached || null);
    idbGetRoom(roomId).then(info => { if (info) setCustomer(prev => prev || info); }).catch(() => {});

    const loadCustomer = async () => {
      try {
        // Get room info directly by room_id
        const roomResponse = await axios.get(`${API}/chat/room/${roomId}`);
        const room = roomResponse.data;

        if (room) {
          const fresh = {
            id: room.customer_id,
            name: room.customer_name,
            phone: room.customer_phone,
            profile_pic: room.customer_profile_pic,
            is_guest: room.customer_is_guest || false,
            role: room.customer_role || 'customer',
            mb_setup: room.mb_setup || null,
            chat_type: room.chat_type || null,
            flag: room.customer_flag || null,
            client_id: room.customer_client_id || null,
            client_address: room.customer_client_address || null
          };
          setCustomer(fresh);
          writeInstantCache('room_customer', roomId, fresh);
          idbSaveRoom(roomId, fresh);
        }
      } catch (error) {
        console.error('Failed to load customer:', error);
      }
    };

    loadCustomer();
  }, [roomId]);

  // Real-time customer identity sync — keeps name/phone/client_id fresh when
  // admin edits from /clients/update/{id}. Prevents stale-name reversion.
  useEffect(() => {
    const onCustomerUpdated = (data) => {
      if (!data) return;
      const sameRoom = data.room_id && data.room_id === roomId;
      const sameCust = data.customer_id && data.customer_id === customer?.id;
      if (!sameRoom && !sameCust) return;
      setCustomer((prev) => prev ? {
        ...prev,
        name: data.customer_name || prev.name,
        phone: data.customer_phone || prev.phone,
        client_id: data.customer_client_id !== undefined ? data.customer_client_id : prev.client_id,
        profile_pic: data.customer_profile_pic || prev.profile_pic,
        mb_setup: data.mb_setup !== undefined ? data.mb_setup : prev.mb_setup,
        flag: data.flag !== undefined ? data.flag : prev.flag,
      } : prev);
    };
    socket.on('customer_updated', onCustomerUpdated);
    return () => { socket.off('customer_updated', onCustomerUpdated); };
  }, [roomId, customer?.id]);

  // Phase 2: Update online status.
  // Socket presence handles offline (via 15s grace on disconnect) so we no
  // longer POST is_online=false on unmount — was causing flicker during
  // route changes.
  useEffect(() => {
    if (user?.id) {
      console.log('[HandlerChat] Setting handler online:', user.id);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      })
      .then(() => console.log('[HandlerChat] ✅ Online status updated'))
      .catch(err => console.error('[HandlerChat] ❌ Online status update failed:', err));
    }
  }, [user]);

  // Get customer online status.
  // Primary source is socket `user_online`/`user_offline` events (see below);
  // this polling loop is now a slow fallback (20s) to reconcile any missed
  // events after transport upgrades / brief disconnects.
  useEffect(() => {
    if (!customer?.id) return;

    const fetchCustomerStatus = async () => {
      try {
        const res = await axios.get(`${API}/users/status/${customer.id}`);
        setCustomerOnlineStatus(prev => {
          if (prev.is_online === res.data.is_online && prev.last_seen === res.data.last_seen) return prev;
          return res.data;
        });
      } catch (err) {
        console.error('Get customer status failed:', err);
      }
    };

    fetchCustomerStatus();
    const interval = setInterval(fetchCustomerStatus, 20000);

    // Real-time presence via socket — the backend broadcasts `user_online` on
    // register_user and `user_offline` after a 15s grace on disconnect. These
    // events carry only { user_id } (no room_id) — global presence.
    const handleOnline = (data) => {
      if (data?.user_id === customer.id) {
        setCustomerOnlineStatus(prev => ({ ...prev, is_online: true }));
      }
    };
    const handleOffline = (data) => {
      if (data?.user_id === customer.id) {
        setCustomerOnlineStatus(prev => ({
          ...prev,
          is_online: false,
          last_seen: new Date().toISOString(),
        }));
      }
    };
    onUserOnline(handleOnline);
    onUserOffline(handleOffline);

    return () => {
      clearInterval(interval);
      offUserOnline(handleOnline);
      offUserOffline(handleOffline);
    };
  }, [customer]);

  // Heartbeat: Update own online status.
  // NOTE (Feb-12-2026): Socket-driven presence — the socket's `register_user`
  // event already flips DB → online and broadcasts `user_online`; disconnect
  // triggers a 15s grace period → offline. The HTTP heartbeat below is kept
  // ONLY as a slow (30s) safety net in case the socket happens to be
  // reconnecting while the tab is still active.
  useEffect(() => {
    if (!user?.id) return;

    // Set online when component mounts (immediate)
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('Online status update failed:', err));

    // Slow HTTP fallback heartbeat every 30 seconds
    const heartbeat = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('Heartbeat failed:', err));
    }, 30000);

    // On unmount we DO NOT push is_online=false anymore — the socket disconnect
    // grace-period handler owns that flip. Pushing false here caused customers
    // to flicker offline during route changes (e.g. handler opens then closes
    // a chat).
    return () => {
      clearInterval(heartbeat);
    };
  }, [user]);

  // Phase 2: Mark messages as read (Real-time with Socket.IO)
  useEffect(() => {
    if (roomId && messages.length > 0 && user?.id) {
      const unreadIds = messages
        .filter(m => {
          // Mark as read if:
          // 1. Not already read
          // 2. Not sent by handler (sent by customer)
          const notRead = !m.read;
          const sentByCustomer = m.sender_role !== 'handler' && m.sender_role !== 'deposit_handler' && m.sender_role !== 'withdrawal_handler';
          // Also handle old messages without sender_role
          const notSentByMe = m.sender_id !== user.id;
          
          return notRead && notSentByMe;
        })
        .map(m => m.id);
      
      if (unreadIds.length > 0) {
        console.log('[HandlerChat] Marking messages as read:', unreadIds.length);
        
        // Socket.IO real-time read receipt
        markMessagesRead(roomId, unreadIds, user.id);
        
        // HTTP API fallback
        axios.post(`${API}/messages/mark-read?user_id=${user.id}`, {
          message_ids: unreadIds
        })
        .then(() => console.log('[HandlerChat] ✅ Messages marked as read'))
        .catch(err => console.error('[HandlerChat] ❌ Mark read failed:', err));
      }
    }
  }, [messages, roomId, user]);

  // Phase 2: Customer typing is now 100% socket-driven via `user_typing`
  // (handleUserTyping / onUserTyping above → setCustomerTyping). The old 500ms
  // HTTP poll of GET /chat/typing was removed — it was extremely chatty and
  // fully redundant with the real-time socket event.

  const sendMessage = async (e, textFromInput = null) => {
    e?.preventDefault();
    
    // Use text from parameter (WhatsAppInput) or from inputRef (form submission)
    const messageText = textFromInput || inputRef.current?.value?.trim();
    
    if (!messageText || !roomId) {
      return;
    }

    // IMMEDIATELY clear input (before API call)
    // Note: WhatsAppInput handles its own clearing via handleSend

    // Session-38 perf: optimistic send. Show the bubble the instant the user
    // hits send with a `pending: true` flag so the UI never feels slow, even
    // on 3G. When the HTTP response comes back we swap the temp doc for the
    // real one (matched by client_message_id). If the socket echo arrives
    // first, it dedups against client_message_id / id — no double bubble.
    const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cmid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const optimisticMsg = {
      id: `optimistic_${clientMessageId}`,
      client_message_id: clientMessageId,
      room_id: roomId,
      sender_id: user.id,
      sender_role: user.role,
      sender_name: user.username || 'You',
      text: messageText,
      image_url: null,
      voice_url: null,
      timestamp: new Date().toISOString(),
      read_status: 'sending',
      pending: true,
      reply_to: replyingTo ? {
        message_id: replyingTo.id,
        text: replyingTo.text || '(Media)',
        sender_name: replyingTo.sender_name,
      } : undefined,
    };
    notifiedMessageIds.current.add(optimisticMsg.id);
    setMessages(prev => [...prev, optimisticMsg]);
    setIsUserScrolling(false);
    const replyToSnapshot = replyingTo;
    setReplyingTo(null);

    try {
      // Prepare message data
      const messageData = {
        room_id: roomId,
        sender_id: user.id,
        text: messageText,
        image_url: null,
        voice_url: null,
        client_message_id: clientMessageId,
      };

      // Add reply reference if replying to a message
      if (replyToSnapshot) {
        messageData.reply_to = {
          message_id: replyToSnapshot.id,
          text: replyToSnapshot.text || '(Media)',
          sender_name: replyToSnapshot.sender_name
        };
      }

      const response = await axios.post(`${API}/chat/send-message`, messageData);

      // Swap the optimistic bubble for the real doc. Socket echo may have
      // already replaced it — if so, just drop the temp.
      notifiedMessageIds.current.add(response.data.id);
      setMessages(prev => {
        const withoutOptimistic = prev.filter(m => m.id !== optimisticMsg.id);
        if (withoutOptimistic.some(m => m.id === response.data.id)) return withoutOptimistic;
        return [...withoutOptimistic, response.data];
      });
      
      console.log('✅ Handler message sent:', messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Mark the optimistic bubble as failed so the user knows to retry.
      setMessages(prev => prev.map(m => (
        m.id === optimisticMsg.id ? { ...m, pending: false, send_failed: true, read_status: 'failed' } : m
      )));
    }
      
      // Phase 2: Stop typing indicator (Socket.IO + HTTP)
      sendTypingIndicator(roomId, user.id, false);
      if (typingTimeout) clearTimeout(typingTimeout);
      axios.post(`${API}/chat/typing?user_id=${user.id}`, {
        room_id: roomId,
        is_typing: false
      }).catch(err => console.error('Typing update failed:', err));

  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Handler for WhatsAppInput typing indicator
  const handleTypingIndicator = (isTyping) => {
    if (roomId && user?.id) {
      sendTypingIndicator(roomId, user.id, isTyping);
      axios.post(`${API}/chat/typing?user_id=${user.id}`, {
        room_id: roomId,
        is_typing: isTyping
      }).catch(err => console.error('Typing update failed:', err));
      
      if (isTyping) {
        if (typingTimeout) clearTimeout(typingTimeout);
        
        const timeout = setTimeout(() => {
          sendTypingIndicator(roomId, user.id, false);
          axios.post(`${API}/chat/typing?user_id=${user.id}`, {
            room_id: roomId,
            is_typing: false
          }).catch(err => console.error('Typing stop failed:', err));
        }, 3000);
        
        setTypingTimeout(timeout);
      }
    }
  };

  // Handler for WhatsAppInput send message
  const handleWhatsAppSendMessage = (text) => {
    sendMessage(null, text);
  };

  const handleProfileClick = () => {
    if (!customer) return;
    // #3 — on desktop, an embedded chat opens the right-side Customer Details
    // panel instead of the full-screen profile. Mobile keeps the full profile.
    if (embeddedRoomId && shouldRenderDesktop()) {
      window.dispatchEvent(new CustomEvent('dws:open-customer-details', {
        detail: { customerId: customer.id, roomId: roomId, actorId: user.id },
      }));
      return;
    }
    navigate(`/handler-profile/${customer.id}?from_room=${roomId}`, { replace: true });
  };

  // Long press handlers
  const touchStartPos = useRef(null);
  
  const handleTouchStart = (messageId, e) => {
    // Don't trigger on child interactive elements
    if (e.target.tagName === 'IMG' || e.target.tagName === 'AUDIO' || e.target.closest('audio')) {
      return;
    }
    
    // Check if touches array exists
    if (!e.touches || e.touches.length === 0) {
      return;
    }
    // Skip long-press during pinch or when page is currently zoomed —
    // prevents accidental reaction/delete menu while user pans a zoomed view.
    if (e.touches.length > 1) return;
    try {
      const vv = window.visualViewport;
      if (vv && vv.scale > 1.05) return;
    } catch (_) {}
    
    // Store initial touch position
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    
    longPressTimer.current = setTimeout(() => {
      setLongPressMenu({
        messageId,
        x: touchStartPos.current.x,
        y: touchStartPos.current.y
      });
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    touchStartPos.current = null;
  };

  const handleTouchMove = (e) => {
    // Cancel long press only if finger moves more than 10px
    if (longPressTimer.current && touchStartPos.current && e.touches && e.touches.length > 0) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.current.x);
      const deltaY = Math.abs(currentY - touchStartPos.current.y);
      
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  };

  // Double tap for emoji
  const lastTap = useRef(0);
  const handleDoubleTap = (messageId) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setShowEmojiPicker(messageId);
    }
    lastTap.current = now;
  };

  // Message actions
  const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText).then(() => {
      toast.success('Copied', { duration: 1500, style: { background: 'rgba(37,211,102,0.9)', color: '#fff', fontSize: '13px', padding: '6px 16px', minHeight: 'auto' } });
    }).catch(() => {});
    setLongPressMenu(null);
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/chat/message/${messageId}/reaction`, null, {
        params: { user_id: user.id, emoji }
      });

      // Update local state
      setMessages(messages.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || {};
          if (reactions[emoji] && reactions[emoji].includes(user.id)) {
            // Remove reaction
            reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
            if (reactions[emoji].length === 0) delete reactions[emoji];
          } else {
            // Add reaction
            if (!reactions[emoji]) reactions[emoji] = [];
            reactions[emoji].push(user.id);
          }
          return { ...msg, reactions };
        }
        return msg;
      }));

      setShowEmojiPicker(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setLongPressMenu(null);
    setContextMoreOpen(false);
    setShowDeleteModal(true);
  };

  const handleDeleteMessage = async (deleteForEveryone) => {
    if (!messageToDelete) return;
    const targetId = messageToDelete;
    setShowDeleteModal(false);
    setMessageToDelete(null);
    if (deleteForEveryone) {
      // Handler never keeps the original after delete — show placeholder at once.
      setMessages(prev => prev.map(m => m.id === targetId ? {
        ...m, deleted_for_everyone: true, is_deleted_view: true,
        text: `🚫 Deleted message (by ${user?.username || 'handler'})`,
        image_url: null, voice_url: null, document_url: null, is_sticker: false,
        deleted_by_meta: { by: user?.username || 'handler', role: user?.role || 'handler' },
      } : m));
      idbMarkDeleted(targetId);
    } else {
      setMessages(prev => prev.filter(m => m.id !== targetId));
      idbDeleteMessage(targetId);
    }
    try {
      await axios.delete(`${API}/chat/message/${targetId}`, {
        params: { user_id: user.id, delete_for_everyone: deleteForEveryone }
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete message');
      loadMessages(); // resync from server on failure
    }
  };

  const handleReplyMessage = (msg) => {
    setReplyingTo(msg);
    setLongPressMenu(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleForward = async (messageId) => {
    try {
      const msg = messages.find(m => m.id === messageId);
      if (!msg) return;
      
      setMessageToForward(msg);
      
      // Point 7 — load BOTH chats and groups for the unified Forward modal.
      const [chatRes, groupRes] = await Promise.allSettled([
        axios.get(`${API}/chat/rooms`, { params: { user_id: user.id, role: user.role, chat_type: user.chat_type } }),
        axios.get(`${API}/groups/list`, { params: { user_id: user.id } }),
      ]);
      const otherChats = (chatRes.status === 'fulfilled' ? chatRes.value.data : []).filter(chat => chat.id !== roomId);
      setAvailableChats(otherChats);
      setAvailableGroups(groupRes.status === 'fulfilled' ? (groupRes.value.data || []) : []);
      setShowForwardModal(true);
      setLongPressMenu(null);
    } catch (error) {
      console.error('Failed to load forward targets:', error);
      toast.error('Failed to load chats');
    }
  };

  // Point 7 — unified forward to any mix of chats + groups.
  const forwardSelected = async ({ chatIds = [], groupIds = [] }) => {
    try {
      if (!messageToForward) return;
      const m = messageToForward;

      const chatTasks = chatIds.map((rid) =>
        axios.post(`${API}/chat/send-message`, {
          room_id: rid,
          sender_id: user.id,
          text: m.is_sticker ? '' : (m.text || '📷 Forwarded media'),
          image_url: m.image_url,
          voice_url: m.voice_url,
          is_sticker: !!m.is_sticker,
        })
      );
      const groupTasks = groupIds.map((gid) =>
        axios.post(`${API}/groups/${gid}/send-message`, {
          group_id: gid,
          sender_id: user.id,
          sender_name: user.username || user.name,
          sender_role: user.role,
          text: m.is_sticker ? '' : (m.text || ''),
          image_url: m.image_url || null,
          voice_url: m.voice_url || null,
          is_sticker: !!m.is_sticker,
          is_forwarded: true,
        })
      );

      const results = await Promise.allSettled([...chatTasks, ...groupTasks]);
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;

      setShowForwardModal(false);
      setMessageToForward(null);

      if (okCount > 0 && failCount === 0) {
        toast.success(okCount === 1 ? 'Message forwarded!' : `Forwarded to ${okCount} chats/groups`);
      } else if (okCount > 0 && failCount > 0) {
        toast.warning(`Forwarded to ${okCount}, failed for ${failCount}`);
      } else {
        toast.error('Failed to forward');
      }
    } catch (error) {
      console.error('Forward failed:', error);
      toast.error('Failed to forward');
    }
  };

  const handleMoveChat = async (newType) => {
    try {
      const res = await axios.post(`${API}/chat/rooms/${roomId}/move`, {
        new_chat_type: newType,
        user_id: user.id
      });
      toast.success(`Customer moved to ${newType}!`);
      setShowMoveMenu(false);
      setShowMenu(false);
      // Handler stays on OWN dashboard (don't follow customer to new panel)
      if (onBackToList) {
        onBackToList();
      } else {
        navigate('/handler-dashboard');
      }
    } catch (e) {
      toast.error('Failed to move customer');
    }
  };

  const handleAssignMB = async (mb) => {
    if (!customer?.id) return;
    try {
      await axios.patch(`${API}/clients/${customer.id}/mb-setup`, {
        mb_setup: mb,
        actor_id: user.id
      });
      setCustomer((prev) => (prev ? { ...prev, mb_setup: mb } : prev));
      toast.success(mb ? `Assigned to ${mb}` : 'MB tag removed');
      setShowMBMenu(false);
      setShowMenu(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign MB setup');
    }
  };

  const handleMediaSelected = async (media) => {
    let optimisticId = null;
    try {
      let messageText = '';
      let imageUrl = null;
      let voiceUrl = null;
      let documentUrl = null;
      let documentName = null;
      let localPreviewUrl = null;
      let isSticker = false;
      
      if (media.type === 'image') {
        messageText = media.caption || '';
        imageUrl = media.url;
        // Create local preview for optimistic UI
        if (media.file) {
          localPreviewUrl = URL.createObjectURL(media.file);
        }
      } else if (media.type === 'video') {
        messageText = media.caption || '🎥 Video';
        imageUrl = media.url;
      } else if (media.type === 'voice') {
        messageText = '🎤 Voice';
        voiceUrl = media.url;
      } else if (media.type === 'sticker') {
        messageText = '';
        imageUrl = media.url;
        isSticker = true;
      } else if (media.type === 'document') {
        messageText = '📄 ' + (media.fileName || 'Document');
        documentUrl = media.url;
        documentName = media.fileName || 'Document';
      } else if (media.type === 'text') {
        messageText = media.text || '';
      }
      
      // Optimistic UI: Show message immediately with local preview.
      // Session-48i (Bug B1): stamp `client_message_id` + `pending: true`
      // on the optimistic bubble so the socket handler (line ~648) can
      // find it and REPLACE (not duplicate) when the server echoes back.
      // Previously the optimistic used `_uploading` + a random id, so the
      // socket message pushed a 2nd bubble → 2 photos on one send.
      const clientMessageId = media.client_message_id || `cmid-${user.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      optimisticId = 'optimistic-' + Date.now();
      if (localPreviewUrl && media.type === 'image') {
        const optimisticMsg = {
          id: optimisticId,
          client_message_id: clientMessageId,
          pending: true,
          sender_id: user.id,
          sender_name: user.username,
          text: messageText,
          image_url: localPreviewUrl,
          created_at: new Date().toISOString(),
          _uploading: true
        };
        setMessages(prev => [...prev, optimisticMsg]);
        setIsUserScrolling(false);
      }
      
      const response = await axios.post(`${API}/chat/send-message`, {
          room_id: roomId,
          sender_id: user.id,
          text: messageText,
          image_url: imageUrl,
          voice_url: voiceUrl,
          document_url: documentUrl,
          document_name: documentName,
          is_sticker: isSticker,
          client_message_id: clientMessageId, // dedup on server + socket
          view_once: media.view_once && (media.type === 'image' || media.type === 'video' || media.type === 'voice'),
        });

      // Replace optimistic message with real one (dedup safe).
      // Bug #3 fix (Session-53) — unified dedupe. Match by server id OR
      // client_message_id so a socket echo that arrived first is properly
      // recognised as "already inserted" and we don't duplicate the bubble.
      notifiedMessageIds.current.add(response.data.id);
      const cmid = response.data.client_message_id;
      const isDupe = (m) => m.id === response.data.id || (cmid && m.client_message_id === cmid);
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
        setMessages(prev => {
          // Socket already inserted / replaced by id or cmid?
          if (prev.some(m => m.id !== optimisticId && isDupe(m))) {
            return prev.filter(m => m.id !== optimisticId);
          }
          return prev.map(m => m.id === optimisticId ? response.data : m);
        });
      } else {
        setMessages(prev => {
          if (prev.some(isDupe)) return prev;
          return [...prev, response.data];
        });
      }
      setIsUserScrolling(false);
    } catch (error) {
      console.error('Failed to send media:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to send media';
      toast.error(msg);
      // Session-48j: instead of nuking the optimistic bubble, mark it as
      // errored + attach the original media so a retry pill can re-send.
      setMessages(prev => prev.map(m => (
        m.id === optimisticId
          ? { ...m, _uploading: false, _error: true, _retryMedia: media }
          : m
      )));
    }
  };

  // Session-48j — Retry a previously-failed media send. Re-invokes the same
  // handleMediaSelected flow so it goes through URL validation + client_id
  // stamping identical to a fresh send.
  const retryFailedMedia = (msg) => {
    if (!msg?._retryMedia) return;
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    handleMediaSelected(msg._retryMedia);
  };

  // Loading state removed - UI renders instantly

  return (
    <div ref={containerRef} className={`${embeddedRoomId ? 'relative flex-1 min-h-0' : 'fixed inset-0'} bg-gray-100 overflow-hidden`}>
      {/* Header - WhatsApp Style */}
      <div ref={chatHeaderRef} className="absolute top-0 left-0 right-0 bg-[#f0f2f5] border-b border-[#e9edef] z-50">
        <div className="flex items-center min-h-[56px] px-2">
          {/* Back Button */}
          <button
            onClick={() => {
              if (onBackToList) { onBackToList(); return; }
              // Point 10 — return to the full-screen N (New Members) view when
              // the chat was opened from there.
              if (location.state?.fromNewMembers) { navigate('/new-members'); return; }
              if (location.state?.from) { navigate(location.state.from, { replace: true }); return; }
              // Session-35 (revised): Chat ke back button ka ONE consistent
              // job hai — outer dashboard chat-list par le jao. `navigate(-1)`
              // agar user Profile se wapas aaya ho to Profile par le jata
              // tha; user ne clearly bola ki chat se back = dashboard exit.
              // Dashboard state (filter/tab/mb) sessionStorage se restore
              // ho jata hai — koi layout flip nahi.
              const _homeRoute = (user?.role === 'calling_handler') ? '/calling-handler-dashboard' : '/handler-dashboard';
              navigate(_homeRoute, { state: { chatTypeTab: location.state?.chatTypeTab } });
            }}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            data-testid="handler-chat-back-button"
          >
            <ArrowLeft size={24} />
          </button>

          {/* Profile Section - Clickable. Point 48 — the Calling Handler must
              not reach the full client-details profile page ("no dashboard
              access to client details"), so this becomes a plain, inert
              container for that role instead of a navigating button. */}
          <button
            onClick={isReadOnlyViewer ? undefined : handleProfileClick}
            disabled={isReadOnlyViewer}
            className={`flex items-center gap-3 flex-1 min-w-0 py-1 px-2 rounded-lg transition-colors overflow-hidden ${isReadOnlyViewer ? 'cursor-default' : 'hover:bg-gray-50'}`}
          >
            {/* Profile Picture */}
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="chat-header-avatar">
              {customer?.profile_pic ? (
                <img 
                  src={`${API}${customer.profile_pic}`} 
                  alt={customer.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center text-gray-600 font-semibold text-lg ${customer?.profile_pic ? 'hidden' : ''}`}>
                {customer?.name ? customer.name.charAt(0).toUpperCase() : 'C'}
              </div>
            </div>
            
            {/* Name and Status */}
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <h2 className="text-base font-semibold text-gray-900 truncate min-w-0 max-w-full" title={customer?.name || ''}>{truncateName(customer?.name || 'Customer')}</h2>
                {customer?.flag && (
                  <span className="flex-shrink-0"><FlagIcons flag={customer.flag} size={13} /></span>
                )}
                <span className="flex-shrink-0"><MBSetupBadge value={customer?.mb_setup} /></span>
                {customer?.client_id && (
                  <span
                    className="text-[9px] font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full uppercase tracking-wide max-w-[80px] truncate flex-shrink-0"
                    data-testid="chat-header-client-id"
                    title={`Client ID: ${customer.client_id}`}
                  >
                    {customer.client_id}
                  </span>
                )}
              </div>
              <div className="flex items-center">
                <OnlineStatus isOnline={customerOnlineStatus.is_online} lastSeen={customerOnlineStatus.last_seen} showText={true} textColor="text-gray-500" textSize="text-xs" />
              </div>
              {!isOnline && (
                <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="offline-cache-indicator">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Offline — showing saved chats
                </span>
              )}
              {customer?.client_address && (customer.client_address.city || customer.client_address.state) && (
                <p
                  className="text-[11px] text-blue-700 truncate leading-tight"
                  title={customer.client_address.formatted || ''}
                  data-testid="chat-header-client-address"
                >
                  📍 {[customer.client_address.city, customer.client_address.state].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </button>

          {/* Assign-MB quick picker — rendered OUTSIDE the profile <button> so
              the outer button doesn't swallow the picker's click (nested
              <button> is invalid HTML and blocks the inner click). Point 48 —
              a write action, hidden for the read-only Calling Handler. */}
          {!isReadOnlyViewer && !customer?.mb_setup && customer?.id && !customer?.is_guest && customer?.role === 'customer' && (
            <div className="mr-1 flex-shrink-0">
              <AssignMBQuickPicker
                customerId={customer.id}
                currentMB={customer?.mb_setup}
                actorId={user?.id}
                actorRole={user?.role}
                onAssigned={(mb) => setCustomer((prev) => (prev ? { ...prev, mb_setup: mb } : prev))}
              />
            </div>
          )}

          {/* Audio Call Button - Redirect to Customer's Phone Dialpad.
              Point 48 — hidden for the read-only Calling Handler; that role
              already has its own dial/WhatsApp actions on the directory
              screen with proper activity logging. */}
          {!isReadOnlyViewer && (
          <button
            onClick={() => {
              if (!customer?.phone) { toast.error('Customer phone not available'); return; }
              // Point 47 — log the tap as a staff-only chat entry (never
              // visible to the customer). Fire-and-forget so the dial pad
              // opens instantly; append it to this staff member's own view
              // optimistically since it isn't broadcast live.
              axios.post(`${API}/chat/room/${roomId}/call-log`, { user_id: user.id })
                .then(res => setMessages(prev => (prev.some(m => m.id === res.data.id) ? prev : [...prev, res.data])))
                .catch(() => {});
              window.location.href = `tel:${customer.phone}`;
            }}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-700 flex-shrink-0"
            data-testid="audio-call-btn"
          >
            <Phone size={22} />
          </button>
          )}

          {/* #6 — Calling Handler header: show BOTH Call + WhatsApp icons
              (view-only role). Call → device dial pad (with staff activity log);
              WhatsApp → wa.me redirect. */}
          {isReadOnlyViewer && (
            <>
              <button
                onClick={() => {
                  if (!customer?.phone) { toast.error('Customer phone not available'); return; }
                  const phone = String(customer.phone).replace(/\D/g, '');
                  window.open(`https://wa.me/${phone}`, '_blank');
                }}
                className="p-2 hover:bg-gray-100 rounded-full text-green-600 flex-shrink-0"
                title="Open WhatsApp"
                data-testid="calling-header-whatsapp-btn"
              >
                <WhatsappLogo size={22} weight="fill" />
              </button>
              <button
                onClick={() => {
                  if (!customer?.phone) { toast.error('Customer phone not available'); return; }
                  axios.post(`${API}/chat/room/${roomId}/call-log`, { user_id: user.id })
                    .then(res => setMessages(prev => (prev.some(m => m.id === res.data.id) ? prev : [...prev, res.data])))
                    .catch(() => {});
                  window.location.href = `tel:${String(customer.phone).replace(/[^\d+]/g, '')}`;
                }}
                className="p-2 hover:bg-gray-100 rounded-full text-[#008069] flex-shrink-0"
                title="Call"
                data-testid="calling-header-call-btn"
              >
                <Phone size={22} weight="fill" />
              </button>
            </>
          )}
          {!isReadOnlyViewer && (
          <div ref={menuRef} className="relative flex-shrink-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            >
              <DotsThreeVertical size={24} />
            </button>
            
            {/* Dropdown Menu */}
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                {/* Session-48c: Create Request + Mark-as-Solved moved to the
                    top header strip (ChatHeaderActions) — the 3-dot menu now
                    only keeps Cancel Request (destructive action).  */}
                {(customer?.chat_type === 'deposit' || customer?.chat_type === 'withdrawal' || customer?.chat_type === 'support') && (
                  <>
                    <button
                      onClick={async () => {
                        setShowMenu(false);
                        try {
                          const backendType = customer?.chat_type === 'withdrawal' ? 'withdraw' : customer?.chat_type;
                          const res = await axios.get(`${API}/service-requests/open/${customer?.id}`, { params: { type: backendType } });
                          const list = res.data?.items || [];
                          if (list.length === 0) { toast.error('No open requests to cancel'); return; }
                          setSrCancelList(list);
                          setShowSRCancelModal(true);
                        } catch (e) { toast.error('Failed to load'); }
                      }}
                      className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid="handler-sr-cancel-btn"
                    >
                      <span className="text-lg">❌</span> Cancel Request
                    </button>
                    <div className="border-t my-1" />
                  </>
                )}
                {/* Session-47: Theme toggle removed from customer 3-dot menu.
                    Users can change theme from Settings → Appearance instead. */}
                <button
                  onClick={() => {
                    handleProfileClick();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <UserCircle size={20} />
                  View Profile
                </button>
                <button
                  onClick={async () => {
                    setShowMenu(false);
                    try {
                      const next = !isChatMuted;
                      await axios.post(`${API}/chat/room/${roomId}/mute`, { user_id: user.id, is_muted: next });
                      setIsChatMuted(next);
                      toast(next ? 'Chat muted' : 'Chat unmuted', { duration: 1500 });
                    } catch (e) { toast.error('Failed to update mute setting'); }
                  }}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  data-testid="mute-chat-btn"
                >
                  {isChatMuted ? <BellSlash size={20} /> : <Bell size={20} />}
                  {isChatMuted ? 'Unmute Chat' : 'Mute Chat'}
                </button>
                {customer?.is_guest && (
                  <button
                    onClick={() => {
                      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
                      setShowRegisterModal(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-blue-600 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Plus size={20} />
                    Register Customer
                  </button>
                )}
                {!customer?.is_guest && customer?.role === 'customer' && (
                  <button
                    onClick={async () => {
                      setShowMenu(false);
                      try {
                        const res = await axios.get(`${API}/clients/registered`);
                        const fullClient = res.data.find(c => c.id === customer.id);
                        if (fullClient) {
                          setEditClientData(fullClient);
                          setShowEditModal(true);
                        } else {
                          toast.error('Client data not found');
                        }
                      } catch (e) {
                        toast.error('Failed to load client data');
                      }
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FileText size={20} />
                    Edit
                  </button>
                )}
                <button
                  onClick={() => {
                    toast('Block feature coming soon');
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Prohibit size={20} />
                  Block Customer
                </button>
                {/* Session-48c: Mark-as-Solved moved into the top ChatHeaderActions
                    strip. Removed from the 3-dot menu to avoid duplicate CTAs. */}
                {/* Move Chat */}
                <div className="border-t mt-1 pt-1">
                  <button
                    onClick={() => setShowMoveMenu(!showMoveMenu)}
                    className="w-full px-4 py-2 text-left text-orange-600 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <ArrowsLeftRight size={20} />
                    Move to...
                  </button>
                  {showMoveMenu && (
                    <div className="bg-gray-50 py-1">
                      {['deposit', 'withdrawal', 'support', 'inquiry']
                        .filter(type => type !== (user.chat_type || ''))
                        .map(type => (
                        <button key={type} onClick={() => handleMoveChat(type)}
                          className="w-full px-6 py-1.5 text-left text-sm text-gray-700 hover:bg-orange-50 capitalize">
                          {type}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assign to MB Setup — Phase C: never for New User (guest) chats */}
                {!customer?.is_guest && customer?.role === 'customer' && (
                  <div className="border-t mt-1 pt-1">
                    <button
                      onClick={() => setShowMBMenu(!showMBMenu)}
                      className="w-full px-4 py-2 text-left text-teal-700 hover:bg-gray-100 flex items-center justify-between"
                      data-testid="assign-mb-menu-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Star size={20} />
                        Assign to MB Setup
                      </span>
                      {customer?.mb_setup && <MBSetupBadge value={customer.mb_setup} />}
                    </button>
                    {showMBMenu && (
                      <div className="bg-gray-50 py-1">
                        {MB_SETUPS.map((mb) => (
                          <button
                            key={mb}
                            onClick={() => handleAssignMB(mb)}
                            className={`w-full px-6 py-1.5 text-left text-sm hover:bg-teal-50 flex items-center justify-between ${customer?.mb_setup === mb ? 'font-semibold text-teal-700' : 'text-gray-700'}`}
                            data-testid={`assign-mb-${mb}`}
                          >
                            <span>{mb}</span>
                            {customer?.mb_setup === mb && <Check size={14} />}
                          </button>
                        ))}
                        {customer?.mb_setup && (
                          <button
                            onClick={() => handleAssignMB(null)}
                            className="w-full px-6 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                            data-testid="assign-mb-clear"
                          >
                            Clear (remove tag)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>
        {/* Feature 3+5: Chat header action strip (Coin Cut / Deposit).
            Point 48 — this strip triggers message sends (coin cut / deposit
            entries, create request, mark solved) so it's hidden entirely
            for the read-only Calling Handler. */}
        {customer?.id && !isReadOnlyViewer && (
          <ChatHeaderActions
            chatType={location.state?.chatTypeTab || customer?.chat_type || (user.role === 'deposit_handler' || user.chat_type === 'deposit' ? 'deposit' : 'withdrawal')}
            actorId={user.id}
            actorRole={user.role}
            customerId={customer.id}
            roomId={roomId}
            customerFlag={customer?.flag || null}
            onFlagChanged={(next) => setCustomer((prev) => (prev ? { ...prev, flag: next } : prev))}
            onMessageSent={() => loadMessagesRef.current && loadMessagesRef.current()}
            onCreateRequest={() => { setSrAmount(''); setSrReference(''); setShowSRCreateModal(true); }}
            onMarkSolved={(reqId) => { setMarkSolvedReqId(reqId || null); setMarkSolvedOpen(true); }}
          />
        )}
      </div>

      {false && pinnedMessages.length > 0 && (
        <div className="absolute left-0 right-0 bg-white border-b shadow-sm z-20" style={{ top: `${headerHeight}px` }} data-testid="pinned-message-banner">
          {pinnedMessages.map((pm) => (
            <div
              key={pm.id}
              className="px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              onClick={() => {
                const el = document.getElementById(`msg-${pm.id}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              <PushPin size={18} className="text-blue-500 flex-shrink-0" weight="fill" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-blue-600 font-semibold">Pinned Message</p>
                <p className="text-sm text-gray-600 truncate">{pm.text || '📷 Media'}</p>
              </div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await axios.delete(`${API}/chat/room/${roomId}/pin/${pm.id}`);
                    setPinnedMessages((prev) => prev.filter((m) => m.id !== pm.id));
                    toast('Unpinned', { duration: 1000 });
                  } catch (err) {}
                }}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      )}
      {false && pinDurationTarget && (
        <PinDurationPicker
          onClose={() => setPinDurationTarget(null)}
          onPick={async (duration) => {
            try {
              await axios.post(`${API}/chat/room/${roomId}/pin`, { message_id: pinDurationTarget, user_id: user.id, duration });
              const msg = messages.find(m => m.id === pinDurationTarget);
              if (msg) setPinnedMessages(prev => [...prev, msg]);
              toast('Message pinned', { duration: 1500 });
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to pin message');
            }
            setPinDurationTarget(null);
          }}
        />
      )}

      {/* Messages - Fixed position area */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="absolute left-0 right-0 overflow-y-auto p-3 bg-[#efeae2] overscroll-contain msg-noselect"
        style={{
          top: pinnedMessages.length > 0 ? `${headerHeight + pinnedMessages.length * 56}px` : `${headerHeight}px`,
          bottom: '56px',
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'412\' height=\'412\' viewBox=\'0 0 412 412\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23d6d0c5\' fill-opacity=\'0.4\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'2\'/%3E%3Ccircle cx=\'100\' cy=\'50\' r=\'1.5\'/%3E%3Ccircle cx=\'200\' cy=\'30\' r=\'2\'/%3E%3Ccircle cx=\'300\' cy=\'60\' r=\'1.5\'/%3E%3Ccircle cx=\'60\' cy=\'120\' r=\'2\'/%3E%3Ccircle cx=\'160\' cy=\'100\' r=\'1.5\'/%3E%3Ccircle cx=\'260\' cy=\'130\' r=\'2\'/%3E%3Ccircle cx=\'360\' cy=\'110\' r=\'1.5\'/%3E%3Ccircle cx=\'40\' cy=\'200\' r=\'2\'/%3E%3Ccircle cx=\'140\' cy=\'220\' r=\'1.5\'/%3E%3Ccircle cx=\'240\' cy=\'190\' r=\'2\'/%3E%3Ccircle cx=\'340\' cy=\'210\' r=\'1.5\'/%3E%3Ccircle cx=\'80\' cy=\'300\' r=\'2\'/%3E%3Ccircle cx=\'180\' cy=\'280\' r=\'1.5\'/%3E%3Ccircle cx=\'280\' cy=\'310\' r=\'2\'/%3E%3Ccircle cx=\'380\' cy=\'290\' r=\'1.5\'/%3E%3Ccircle cx=\'50\' cy=\'380\' r=\'2\'/%3E%3Ccircle cx=\'150\' cy=\'360\' r=\'1.5\'/%3E%3Ccircle cx=\'250\' cy=\'390\' r=\'2\'/%3E%3Ccircle cx=\'350\' cy=\'370\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")'
        }}
      >
        <div className="space-y-[3px] pb-4">
          {/* Older messages loader (top of chat) */}
          {olderLoading && (
            <div className="flex items-center justify-center py-2" data-testid="older-loading">
              <div className="bg-white/90 text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-full shadow-sm inline-flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-[#54656f] border-t-transparent rounded-full animate-spin"></span>
                Loading older messages…
              </div>
            </div>
          )}
          {!hasMoreOlder && messages.length > 0 && (
            <div className="flex items-center justify-center py-2" data-testid="older-end">
              <div className="bg-white/70 text-[#54656f] text-[10px] font-medium px-3 py-1 rounded-full">
                Start of conversation
              </div>
            </div>
          )}
          {/* Session-38 perf: skeleton bubbles while the initial fetch is in
              flight. Removes the blank-screen flash on chat open and makes
              the chat feel instant even on slow networks. */}
          {messages.length === 0 && isInitialLoadRef.current && (
            <div className="space-y-2 py-4" data-testid="chat-skeleton">
              {[
                { align: 'start', w: 'w-40' },
                { align: 'end',   w: 'w-56' },
                { align: 'start', w: 'w-52' },
                { align: 'end',   w: 'w-36' },
                { align: 'start', w: 'w-44' },
              ].map((s, i) => (
                <div key={i} className={`flex ${s.align === 'end' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`${s.w} h-8 rounded-lg animate-pulse ${
                    s.align === 'end' ? 'bg-[#d9fdd3]/60' : 'bg-white/70'
                  }`} />
                </div>
              ))}
            </div>
          )}
          {messages.map((msg, idx) => {
            const isOwn = msg.sender_id === user.id || (msg.sender_role && !['customer', 'inquiry_guest', 'support_guest'].includes(msg.sender_role));
            const reactions = msg.reactions || {};
            
            // Skip if this message is part of an image group but NOT the first
            if (isPartOfImageGroup(messages, idx)) return null;
            
            // Check if this starts an image group
            let imageGroup = getImageGroup(messages, idx);
            // Session-53 defence-in-depth — final belt-and-braces filter at
            // render time. Even if `getImageGroup` somehow returned a group
            // containing a sticker (shouldn't per the guards above, but a
            // stale in-flight optimistic bubble without `is_sticker` set
            // could slip through), yank stickers/SVG-data-URIs out of the
            // group before it ever paints. A single-photo "group" then
            // collapses back to a standalone image render below.
            if (imageGroup) {
              imageGroup = imageGroup.filter(g => !isStickerLike(g));
              if (imageGroup.length < 2) imageGroup = null;
            }
            
            // Check if this is the first unread INCOMING message (not own message)
            const isIncoming = !isOwn;
            const isPreviousIncoming = idx > 0 ? (() => { const prev = messages[idx - 1]; return prev?.sender_role ? ['customer', 'inquiry_guest', 'support_guest'].includes(prev.sender_role) : prev?.sender_id !== user.id; })() : false;
            const isFirstUnread = isIncoming && !msg.read && (idx === 0 || messages[idx - 1]?.read !== false || !isPreviousIncoming);
            const unreadCount = messages.filter(m => {
              const mIsOwn = m.sender_id === user.id || (m.sender_role && !['customer', 'inquiry_guest', 'support_guest'].includes(m.sender_role));
              return !mIsOwn && !m.read;
            }).length;
            
            return (
              <React.Fragment key={idx}>
                {/* Date Separator - WhatsApp Style */}
                {shouldShowDateSeparator(messages, idx) && (
                  <div className="flex items-center justify-center my-3">
                    <div className="bg-white/90 text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm" data-testid="date-separator">
                      {getDateLabel(msg.timestamp)}
                    </div>
                  </div>
                )}

                {/* Unread Messages Divider - Only for incoming messages */}
                {isFirstUnread && unreadCount > 0 && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {unreadCount} Unread Message{unreadCount > 1 ? 's' : ''}
                    </div>
                  </div>
                )}
                
              <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className="relative max-w-[85%] group"
                  onMouseEnter={() => setHoveredMsg(msg.id)}
                  onMouseLeave={() => setHoveredMsg(null)}
                >
                  {/* Dropdown arrow */}
                  {hoveredMsg === msg.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLongPressMenu({
                          messageId: msg.id,
                          x: e.clientX,
                          y: e.clientY
                        });
                      }}
                      className="absolute top-1 right-1 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-opacity"
                    >
                      <CaretDown size={12} className="text-gray-600" weight="bold" />
                    </button>
                  )}
                  <div 
                    className={`relative ${msg.is_sticker
                      ? 'bg-transparent shadow-none p-0'
                      : `px-2 pt-1.5 pb-[2px] rounded-lg shadow-sm overflow-hidden ${isOwn ? 'bg-[#d9fdd3]' : 'bg-white'}`
                    }`}
                    style={{
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      WebkitTouchCallout: 'none',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    onContextMenu={(e) => { e.preventDefault(); handleTouchStart(msg.id, e); }}
                    onTouchStart={(e) => handleTouchStart(msg.id, e)}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    onMouseDown={(e) => handleTouchStart(msg.id, { target: e.target, touches: [{ clientX: e.clientX, clientY: e.clientY }] })}
                    onMouseUp={handleTouchEnd}
                    onMouseLeave={handleTouchEnd}
                    onClick={() => handleDoubleTap(msg.id)}
                  >
                    {/* Reply Reference - WhatsApp Style */}
                    {msg.reply_to && (
                      <div className={`mb-1 pl-2 py-0.5 border-l-2 rounded text-xs ${
                        isOwn ? 'border-green-700 bg-green-100/30' : 'border-blue-600 bg-blue-100/30'
                      }`}>
                        <div className="font-semibold text-[10px] opacity-70">
                          {msg.reply_to.sender_name}
                        </div>
                        <div className="truncate text-[11px] opacity-60">
                          {msg.reply_to.text || '📷 Media'}
                        </div>
                      </div>
                    )}
                    
                    {/* Image Group Grid - WhatsApp Style */}
                    {imageGroup && imageGroup.length > 1 && (
                      <div className={`grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden mb-1.5 w-[320px] max-w-full`}>
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => {
                          const gUrl = gMsg.image_url.startsWith('http') ? gMsg.image_url 
                            : gMsg.image_url.startsWith('/api/') ? `${BACKEND_URL}${gMsg.image_url}` 
                            : `${BACKEND_URL}/api${gMsg.image_url}`;
                          return (
                            <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(gUrl)}>
                              <img src={gUrl} alt="" className="w-full h-[110px] object-cover" />
                              {gIdx === 3 && imageGroup.length > 4 && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Group caption below grid */}
                    {imageGroup && imageGroup.length > 1 && (() => {
                      const lastMsg = imageGroup[imageGroup.length - 1];
                      const captionText = lastMsg.caption || (lastMsg.text && lastMsg.text.trim() && lastMsg.text !== '🖼️ Photo' && lastMsg.text !== '🎥 Video' ? lastMsg.text : '');
                      return captionText ? <p className="text-sm mb-1">{captionText}</p> : null;
                    })()}

                    {/* Point 63 — view-once placeholder replaces the normal
                        image/video block once the backend has stripped the
                        URL (not the sender, and either not-yet-opened or
                        already-opened-so-gone-forever). */}
                    {msg.view_once_state && !imageGroup && msg.sender_id !== user.id && msg.view_once_media_type !== 'voice' && (
                      <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/chat/message/${msg.id}/view-once/open`} />
                    )}

                    {/* Single Image or Video — WhatsApp style: flush edges, overlay timestamp when no caption */}
                    {msg.image_url && !imageGroup && (() => {
                      const hasCaption = !!(msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video'));
                      const timeStr = new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
                      // Compact sticker render — no bubble, ~140px
                      if (msg.is_sticker) {
                        return (
                          <img
                            src={buildMediaUrl(msg.image_url)}
                            alt="Sticker"
                            className="w-[88px] max-w-[88px] h-auto object-contain block bg-transparent"
                            data-testid="sticker-message"
                          />
                        );
                      }
                      return (
                        <div className={`relative -mx-2 -mt-1.5 ${hasCaption ? 'mb-1' : 'mb-0'} rounded-t-lg overflow-hidden`}>
                          {/* Point 40 — no `w-full`/`object-cover` here: a
                              single photo/video renders at its own natural
                              aspect ratio (capped by max-w/max-h), matching
                              WhatsApp, instead of being stretched to the
                              bubble's full width and cropped to fit. */}
                          {isVideoUrl(msg.image_url) ? (
                            <video
                              src={buildMediaUrl(msg.image_url)}
                              controls
                              className="max-w-[280px] max-h-[320px] block"
                              preload="metadata"
                              data-testid="video-message"
                            />
                          ) : (
                            <img
                              src={buildMediaUrl(msg.image_url)}
                              alt="Shared"
                              className="w-[180px] h-[180px] object-cover block cursor-pointer hover:opacity-95 transition-opacity"
                              onClick={() => handleImageClick(buildMediaUrl(msg.image_url))}
                              onError={(e) => {
                                console.error('Image load error:', msg.image_url);
                                e.target.style.display = 'none';
                              }}
                              data-testid="photo-message"
                            />
                          )}
                          {/* WhatsApp-style timestamp overlay (only when no caption) */}
                          {!hasCaption && !isVideoUrl(msg.image_url) && (
                            <div className="absolute bottom-1 right-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/45 backdrop-blur-[2px]">
                              <span className="text-[10.5px] text-white leading-none">{timeStr}</span>
                              {isOwn && (() => {
                                if (msg.send_failed || msg._error) return <WarningCircle size={14} weight="bold" className="text-red-400" />;
                                if (msg.pending) return <Clock size={12} weight="regular" className="text-white/80" />;
                                const delivered = msg.delivered !== undefined ? msg.delivered : true;
                                const read = msg.read !== undefined ? msg.read : false;
                                if (read) return <Checks size={13} weight="bold" className="text-[#8ee8ff]" />;
                                if (delivered) return <Checks size={13} weight="bold" className="text-white/80" />;
                                return <Check size={13} weight="bold" className="text-white/80" />;
                              })()}
                            </div>
                          )}
                          {/* Session-48j — Retry pill on failed media send.
                              Overlaid centred on the broken bubble; one tap
                              re-runs the upload + send pipeline with the
                              stored original file. */}
                          {msg._error && msg._retryMedia && (
                            <button
                              onClick={(e) => { e.stopPropagation(); retryFailedMedia(msg); }}
                              className="absolute inset-0 m-auto w-fit h-fit px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold shadow-lg flex items-center gap-1"
                              data-testid={`retry-media-btn-${msg.id}`}
                            >
                              🔄 Retry
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Point 63 — view-once voice placeholder */}
                    {msg.view_once_state && msg.sender_id !== user.id && msg.view_once_media_type === 'voice' && (
                      <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/chat/message/${msg.id}/view-once/open`} />
                    )}

                    {/* Voice Message */}
                    {msg.voice_url && (
                      <VoiceMessage
                        audioUrl={(() => {
                          if (msg.voice_url.startsWith('http')) {
                            return msg.voice_url;
                          } else if (msg.voice_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else if (msg.voice_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}/api${msg.voice_url}`;
                          } else {
                            return `${API}${msg.voice_url}`;
                          }
                        })()}
                        isSender={isOwn}
                      />
                    )}
                    
                    {/* Document Display - Clickable */}
                    {msg.document_url && (
                      <a
                        href={(() => {
                          if (msg.document_url.startsWith('http')) {
                            return msg.document_url;
                          } else if (msg.document_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else if (msg.document_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}/api${msg.document_url}`;
                          } else {
                            return `${API}${msg.document_url}`;
                          }
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText size={24} className="text-white" weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {msg.document_name || 'Document'}
                          </p>
                          <p className="text-xs text-gray-500">Tap to open</p>
                        </div>
                      </a>
                    )}
                    
                    {/* Call Info Bubble - WhatsApp Style */}
                    {msg.call_info ? (
                      <div className="flex items-center gap-2 py-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${msg.call_info.status === 'completed' ? 'bg-green-100' : 'bg-red-100'}`}>
                          <Phone size={16} weight="fill" className={`${msg.call_info.status === 'completed' ? 'text-green-600' : 'text-red-500'} ${msg.call_info.caller_id === user?.id ? 'rotate-[135deg]' : ''}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">Voice call</p>
                          <p className={`text-xs ${msg.call_info.status === 'completed' ? 'text-gray-500' : 'text-red-500'}`}>
                            {msg.call_info.status === 'completed' ? (msg.call_info.duration >= 60 ? `${Math.floor(msg.call_info.duration/60)} min` : `${msg.call_info.duration} secs`) : 'No answer'}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Action Sticker Bubble (Withdraw Done / Deposit Done / Coin Cut) */}
                    {msg.action_sticker && (
                      <div className="py-1"><ActionStickerBubble sticker={msg.action_sticker} /></div>
                    )}
                    
                    {/* Text + WhatsApp-style inline time */}
                    {msg.text && !msg.call_info ? (
                      <div className={`leading-[19px] ${msg.deleted_for_everyone ? 'italic text-slate-400 border-l-2 border-slate-300 pl-2 bg-slate-50/40' : ''} ${msg.text_striked ? 'bg-red-50 border-l-4 border-red-500 pl-2 pr-1 py-1 rounded' : ''}`} data-testid={msg.deleted_for_everyone ? `deleted-msg-${msg.id}` : (msg.text_striked ? `reverted-msg-${msg.id}` : undefined)}>
                        {msg.text_striked && (
                          <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide">
                            ❌ Reverted
                          </div>
                        )}
                        {msg.meta?.bot_options?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5 mb-1" data-testid={`bot-options-${msg.id}`}>
                            {msg.meta.bot_options.map((op, oi) => (
                              op.link ? (
                                <a key={oi} href={op.link} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100"
                                  data-testid={`bot-option-${msg.id}-${oi}`}>
                                  {op.label || `Option ${oi + 1}`}
                                </a>
                              ) : (
                                <span key={oi}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 border border-slate-300 text-slate-700 text-[11px] font-semibold"
                                  title={op.reply || ''}
                                  data-testid={`bot-option-${msg.id}-${oi}`}>
                                  {op.label || `Option ${oi + 1}`}
                                </span>
                              )
                            ))}
                          </div>
                        )}
                        <span className={`text-[14.5px] break-words whitespace-pre-line ${msg.deleted_for_everyone ? 'text-slate-500 italic' : 'text-[#111b21]'} ${msg.text_striked ? 'line-through text-red-800/80' : ''}`}>
                          {msg.text.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
                            if (part.match(/^https?:\/\//)) {
                              return (
                                <a
                                  key={index}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#027eb5] underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {part}
                                </a>
                              );
                            }
                            return part;
                          })}
                        </span>
                        {/* Session-43: Edit / Cancel buttons on deposit_done /
                            coin_cut / withdraw_done notices REMOVED per user
                            request. These messages are now immutable — cancel
                            flow lives in the pending-requests chooser above. */}
                        <span className="float-right ml-[8px] mt-[3px] flex items-center gap-[3px] leading-none">
                          {msg.starred_by?.includes(user.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                          {msg.edited && <span className="text-[10.5px] text-[#667781] italic">edited</span>}
                          <span className="text-[11px] text-[#667781]">
                            {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) 
                              ? new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
                              : ''}
                          </span>
                          {isOwn && (() => {
                            if (msg.send_failed) return <WarningCircle size={16} weight="bold" className="text-red-500" title="Send failed — tap to retry" />;
                            if (msg.pending) return <Clock size={14} weight="regular" className="text-[#667781]" />;
                            const delivered = msg.delivered !== undefined ? msg.delivered : true;
                            const read = msg.read !== undefined ? msg.read : false;
                            if (read) return <Checks size={16} weight="bold" className="text-[#53bdeb]" />;
                            if (delivered) return <Checks size={16} weight="bold" className="text-[#667781]" />;
                            return <Check size={16} weight="bold" className="text-[#667781]" />;
                          })()}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-end items-center gap-[3px] mt-[2px] mb-[1px]">
                        {msg.starred_by?.includes(user.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                        {msg.edited && <span className="text-[10.5px] text-[#667781] italic">edited</span>}
                        <span className="text-[11px] text-[#667781] leading-none">
                          {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) 
                            ? new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
                            : ''}
                        </span>
                        {isOwn && (() => {
                          const delivered = msg.delivered !== undefined ? msg.delivered : true;
                          const read = msg.read !== undefined ? msg.read : false;
                          if (read) return <Checks size={16} weight="bold" className="text-[#53bdeb]" />;
                          if (delivered) return <Checks size={16} weight="bold" className="text-[#667781]" />;
                          return <Check size={16} weight="bold" className="text-[#667781]" />;
                        })()}
                      </div>
                    )}
                  </div>
                  
                  {/* Reactions */}
                  {Object.keys(reactions).length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {Object.entries(reactions).map(([emoji, userIds]) => (
                        <div 
                          key={emoji}
                          className="bg-white rounded-full px-2 py-1 text-xs shadow-sm border flex items-center gap-1"
                        >
                          <span>{emoji}</span>
                          <span className="text-gray-600">{userIds.length}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker === msg.id && (
                    <div className="absolute bottom-full mb-2 bg-white rounded-lg shadow-lg p-2 flex gap-2 z-50">
                      {EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className="text-2xl hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} style={{ overflowAnchor: 'auto', height: '1px' }} />
          
          {/* Typing Indicator */}
          {customerTyping && (
            <div className="flex justify-start">
              <div className="bg-white px-4 py-2 rounded-lg shadow">
                <TypingIndicator username={customer?.name || "Customer"} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to Bottom Arrow - WhatsApp Style */}
      {isUserScrolling && (
        <button
          onClick={() => { setIsUserScrolling(false); scrollToBottom(); }}
          className={`${embeddedRoomId ? 'absolute' : 'fixed'} bottom-24 right-4 z-40 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200`}
          data-testid="scroll-to-bottom-btn"
        >
          <CaretDown size={20} className="text-[#54656f]" weight="bold" />
        </button>
      )}

      {/* Input - WhatsApp Style. Point 48 — the Calling Handler role is
          read-only (backend hard-403s any send from this role); show a
          banner instead of the compose input, mirroring AdminChat.js's
          `viewOnly` banner pattern. */}
      <div className="absolute bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]">
        {isReadOnlyViewer ? (
          <div className="bg-white border-t p-3">
            <div className="bg-green-50 border border-green-200 text-green-700 py-3 px-4 flex items-center justify-center gap-2 rounded-lg shadow-sm">
              <Eye size={20} weight="bold" />
              <span className="font-medium text-sm" data-testid="calling-handler-viewonly-banner">
                View Only — Calling Handler cannot send messages
              </span>
            </div>
          </div>
        ) : (
          <WhatsAppInput
            onSendMessage={handleWhatsAppSendMessage}
            onSendMedia={handleMediaSelected}
            onOpenMediaPicker={() => setShowMediaPicker(true)}
            onTyping={handleTypingIndicator}
            placeholder="Type a message..."
            disabled={false}
            replyingTo={replyingTo}
            onCancelReply={cancelReply}
            userId={user?.id}
            userRole="handler"
          />
        )}
      </div>

      {/* Long Press Menu — WhatsApp-style compact icon bar */}
      {longPressMenu && (() => {
        const msg = messages.find(m => m.id === longPressMenu.messageId);
        if (!msg) return null;
        const isStarred = msg?.starred_by?.includes(user.id);
        const isOwn = msg.sender_id === user.id;
        const barTop = Math.max(60, longPressMenu.y - 70);
        const barLeft = Math.max(12, Math.min(longPressMenu.x - 140, window.innerWidth - 292));
        return (
          <>
            <div className="fixed inset-0 z-40" onClick={() => { setLongPressMenu(null); setContextMoreOpen(false); }} />
            <div
              className="fixed bg-white rounded-full shadow-2xl border border-gray-200 z-50 flex items-center px-1.5 py-1"
              style={{ top: barTop, left: barLeft }}
              data-testid="handler-long-press-icon-bar"
            >
              <button onClick={() => handleReplyMessage(msg)} className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700" title="Reply" data-testid="hlp-reply">
                <ArrowBendUpLeft size={20} weight="bold" />
              </button>
              <button onClick={() => handleForward(longPressMenu.messageId)} className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700" title="Forward" data-testid="hlp-forward">
                <ShareFat size={20} weight="bold" />
              </button>
              <button onClick={() => handleCopyMessage(msg.text)} className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700" title="Copy" data-testid="hlp-copy">
                <Copy size={20} weight="bold" />
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/chat/message/${longPressMenu.messageId}/star?user_id=${user.id}`);
                    setMessages(prev => prev.map(m => {
                      if (m.id === longPressMenu.messageId) {
                        const starred = m.starred_by || [];
                        return res.data.starred
                          ? { ...m, starred_by: [...starred, user.id] }
                          : { ...m, starred_by: starred.filter(id => id !== user.id) };
                      }
                      return m;
                    }));
                    toast(res.data.starred ? 'Message starred' : 'Star removed', { duration: 1500 });
                  } catch (err) { toast.error('Failed to star message'); }
                  setLongPressMenu(null);
                }}
                className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700"
                title="Star" data-testid="hlp-star"
              >
                <Star size={20} weight={isStarred ? 'fill' : 'bold'} className={isStarred ? 'text-yellow-500' : ''} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setContextMoreOpen(v => !v); }} className="p-2.5 hover:bg-gray-100 rounded-full text-gray-700" title="More" data-testid="hlp-more">
                <DotsThreeVertical size={20} weight="bold" />
              </button>
            </div>
            {contextMoreOpen && (
              <div
                className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 z-50 py-1 min-w-[180px]"
                style={{ top: barTop + 48, left: Math.min(barLeft + 180, window.innerWidth - 200) }}
              >
                {/* Point 7 — "Forward to Group" merged into unified Forward
                    modal (long-press Forward now covers chats AND groups). */}
                {/* Handler delete — only real human messages (own/customer),
                    within 10 min, never system/automation content. */}
                {(() => {
                  const _protected = ['deposit_notice','coin_cut_notice','withdraw_done_notice','deposit_request_card','withdraw_request_card','support_request_card','service_request_cancelled_notice'];
                  const isSystem = _protected.includes(msg?.meta?.kind) || !!msg?.action_sticker;
                  const within10 = msg?.timestamp && (Date.now() - new Date(msg.timestamp).getTime()) <= 10 * 60 * 1000;
                  const mine = msg?.sender_id && msg.sender_id === user?.id;
                  const fromCustomer = ['customer','support_guest','inquiry_guest'].includes(msg?.sender_role);
                  const alreadyDeleted = msg?.deleted_for_everyone || msg?.is_deleted_view;
                  const canDelete = !isSystem && within10 && (mine || fromCustomer) && !alreadyDeleted;
                  return canDelete ? (
                    <button onClick={() => handleDeleteClick(longPressMenu.messageId)} className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-red-600 text-sm" data-testid="hlp-delete">
                      <Trash size={16} /> Delete
                    </button>
                  ) : null;
                })()}
              </div>
            )}
          </>
        );
      })()}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Message</h3>
            <p className="text-gray-600 mb-6">Choose delete option:</p>
            <div className="space-y-2">
              <button onClick={() => handleDeleteMessage(false)} className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-left" data-testid="delete-for-me-btn">
                Delete for Me
              </button>
              <button onClick={() => handleDeleteMessage(true)} className="w-full px-4 py-3 bg-red-100 hover:bg-red-200 rounded-lg text-left text-red-600" data-testid="delete-for-everyone-btn">
                Delete for Everyone
              </button>
              <button onClick={() => { setShowDeleteModal(false); setMessageToDelete(null); }} className="w-full px-4 py-3 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forward Modal - unified (chats + groups), Point 7 */}
      <MultiSelectForwardModal
        open={showForwardModal}
        chats={availableChats}
        groups={availableGroups}
        accentColor="#0A6F67"
        onClose={() => {
          setShowForwardModal(false);
          setMessageToForward(null);
        }}
        onForward={forwardSelected}
      />

      {/* MediaPicker */}
      {showMediaPicker && (
        <MediaPicker
          onMediaSelected={handleMediaSelected}
          onClose={() => { setShowMediaPicker(false); setPastedFile(null); }}
          userRole="handler"
          onOpenSavedTexts={() => setShowSavedTextsPanel(true)}
          onOpenSavedPhotos={() => setShowSavedPhotosPanel(true)}
          initialFile={pastedFile}
        />
      )}

      {/* Edit deposit/withdraw entry modal */}
      {editEntry && (
        <EditEntryModal
          open={!!editEntry}
          type={editEntry.type}
          entryId={editEntry.entryId}
          actorId={user.id}
          onClose={() => setEditEntry(null)}
          onEdited={() => { /* socket message_updated will refresh the bubble */ }}
        />
      )}

      {/* Clean reason-prompt modal (replaces window.prompt) */}
      <ReasonPromptModal
        open={!!reasonPrompt}
        title={reasonPrompt?.title || 'Reason'}
        helperText={reasonPrompt?.helper || ''}
        placeholder={reasonPrompt?.placeholder || ''}
        confirmLabel={reasonPrompt?.confirmLabel || 'Submit'}
        onCancel={() => setReasonPrompt(null)}
        onConfirm={(v) => reasonPrompt?.onConfirm?.(v)}
      />

      {/* Saved Texts Panel */}
      {showSavedTextsPanel && (
        <SavedTextsPanel
          userId={user.id}
          userRole="handler"
          onTextSelect={(text) => { sendMessage(null, text); setShowSavedTextsPanel(false); }}
          onClose={() => setShowSavedTextsPanel(false)}
        />
      )}

      {/* Saved Photos Panel */}
      {showSavedPhotosPanel && (
        <SavedPhotosPanel
          userId={user.id}
          onPhotoSelect={(media) => { handleMediaSelected(media); setShowSavedPhotosPanel(false); }}
          onClose={() => setShowSavedPhotosPanel(false)}
          onActionSticker={async ({ type, amount }) => {
            try {
              const resp = await axios.post(`${API}/chat/send-message`, {
                  room_id: roomId,
                  sender_id: user.id,
                  text: null,
                  action_sticker: { type, amount }
                });
              setMessages(prev => prev.some(m => m.id === resp.data.id) ? prev : [...prev, resp.data]);
              // If Coin Cut → trigger Withdraw Group broadcast (Task 5 automation)
              if (type === 'coin_cut') {
                axios.post(`${API}/coin-cut/broadcast`, {
                    actor_id: user.id,
                    customer_id: customer?.id,
                    amount,
                    room_id: roomId
                  }).catch(err => console.error('Coin cut broadcast failed (non-network):', err));
              }
            } catch (err) {
              console.error('Failed to send action sticker:', err);
            }
            setShowSavedPhotosPanel(false);
          }}
        />
      )}

      {/* Image Preview Modal */}
      {showImagePreview && previewImageUrl && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          data-testid="media-viewer"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between p-4 text-white">
            <button onClick={closeImagePreview} className="p-2 hover:bg-white/10 rounded-full">
              <X size={24} weight="bold" />
            </button>
            <span className="text-sm font-medium">
              {allMediaUrls.length > 1 ? `${currentMediaIdx + 1} / ${allMediaUrls.length}` : 'Photo'}
            </span>
            <div className="w-10" />
          </div>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center px-4 relative">
            {allMediaUrls.length > 1 && currentMediaIdx > 0 && (
              <button
                onClick={() => { const idx = currentMediaIdx - 1; setCurrentMediaIdx(idx); setPreviewImageUrl(allMediaUrls[idx]); }}
                className="absolute left-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10"
              >
                <ArrowLeft size={20} weight="bold" />
              </button>
            )}
            <div className="w-full h-full max-h-[75vh] flex-1">
              <ZoomableImage src={previewImageUrl} alt="Preview" />
            </div>
            {allMediaUrls.length > 1 && currentMediaIdx < allMediaUrls.length - 1 && (
              <button
                onClick={() => { const idx = currentMediaIdx + 1; setCurrentMediaIdx(idx); setPreviewImageUrl(allMediaUrls[idx]); }}
                className="absolute right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10"
              >
                <ShareFat size={20} weight="bold" />
              </button>
            )}
          </div>

          {/* Bottom Action Bar - WhatsApp Style */}
          <div className="flex items-center justify-around p-4 border-t border-white/10">
            <button
              onClick={() => { if(navigator.share) navigator.share({url: previewImageUrl}).catch(()=>{}); else { navigator.clipboard.writeText(previewImageUrl); toast('Link copied'); } }}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white"
            >
              <ShareFat size={22} />
              <span className="text-[10px]">Forward</span>
            </button>
            <button
              onClick={closeImagePreview}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white"
            >
              <ArrowLeft size={22} />
              <span className="text-[10px]">Back</span>
            </button>
            <a
              href={previewImageUrl}
              download="photo.jpg"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white"
            >
              <CaretDown size={22} weight="bold" />
              <span className="text-[10px]">Download</span>
            </a>
          </div>
        </div>
      )}

      {/* Register Customer Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Register Customer</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) setRegisterForm({...registerForm, phone: val});
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="text"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Default: Abcd1234"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowRegisterModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRegisterCustomer}
                disabled={registering}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {registering ? 'Registering...' : 'Register'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editClientData && (
        <EditClientModal
          client={editClientData}
          isRegistered={true}
          userId={user?.id}
          onClose={() => {
            setShowEditModal(false);
            setEditClientData(null);
            const loadUpdated = async () => {
              try {
                const roomResponse = await axios.get(`${API}/chat/room/${roomId}`);
                const room = roomResponse.data;
                if (room) {
                  setCustomer({
                    id: room.customer_id,
                    name: room.customer_name,
                    phone: room.customer_phone,
                    profile_pic: room.customer_profile_pic,
                    is_guest: room.customer_is_guest || false,
                    role: room.customer_role || 'customer',
                    mb_setup: room.mb_setup || null,
                    chat_type: room.chat_type || null
                  });
                }
              } catch (e) { console.error(e); }
            };
            loadUpdated();
          }}
        />
      )}

      {/* Point 7 — separate "Forward to Group" modal removed; unified into
          MultiSelectForwardModal above. */}

      {/* Audio Call Screen */}
      {activeCall && (
        <AudioCallScreen
          socket={socket}
          callId={activeCall.callId}
          callerId={activeCall.callerId}
          calleeId={activeCall.calleeId}
          callerName={activeCall.callerName}
          calleeName={activeCall.calleeName}
          callerProfilePic={activeCall.callerProfilePic}
          calleeProfilePic={activeCall.calleeProfilePic}
          isIncoming={activeCall.isIncoming}
          currentUserId={user.id}
          currentUserRole="handler"
          isConference={activeCall.isConference || false}
          onClose={() => setActiveCall(null)}
          API={API}
        />
      )}

      {/* Incoming Call Overlay */}
      {incomingCall && !activeCall && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#1a3a2a] rounded-2xl p-8 max-w-xs w-full text-center" data-testid="incoming-call-modal">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] mx-auto mb-4 flex items-center justify-center overflow-hidden">
              {incomingCall.callerProfilePic ? (
                <img src={`${API}${incomingCall.callerProfilePic}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-white">{(incomingCall.callerName || 'U')[0]?.toUpperCase()}</span>
              )}
            </div>
            <h3 className="text-xl font-bold text-white mb-1">{incomingCall.callerName}</h3>
            <p className="text-white/50 text-sm mb-6">
              {incomingCall.isConference 
                ? `Conference call (${incomingCall.participantNames?.join(', ')})` 
                : 'Incoming audio call...'}
            </p>
            <div className="flex justify-around">
              <button
                onClick={() => {
                  socket.emit('call_reject', { call_id: incomingCall.callId });
                  setIncomingCall(null);
                }}
                className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center"
                data-testid="reject-incoming-btn"
              >
                <Phone size={24} weight="fill" className="text-white rotate-[135deg]" />
              </button>
              <button
                onClick={() => {
                  setActiveCall({
                    callId: incomingCall.callId,
                    callerId: incomingCall.callerId,
                    calleeId: user.id,
                    callerName: incomingCall.callerName,
                    calleeName: user.username,
                    callerProfilePic: incomingCall.callerProfilePic,
                    calleeProfilePic: user.profile_pic,
                    isIncoming: true,
                    isConference: incomingCall.isConference || false
                  });
                  setIncomingCall(null);
                }}
                className="w-14 h-14 rounded-full bg-[#00a884] flex items-center justify-center"
                data-testid="accept-incoming-btn"
              >
                <Phone size={24} weight="fill" className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session-40: Handler-side Service Request creation modal */}
      {showSRCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center p-2"
             onClick={() => { if (!srBusy) { setShowSRCreateModal(false); setSrAmount(''); setSrReference(''); } }}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-4"
               data-testid="handler-sr-create-modal">
            <h3 className="font-bold text-lg mb-3">
              🎫 Create {customer?.chat_type === 'deposit' ? 'Deposit' : customer?.chat_type === 'withdrawal' ? 'Withdraw' : 'Support'} Request
            </h3>
            <p className="text-[11px] text-slate-500 mb-2">On behalf of <b>{customer?.name || customer?.username}</b></p>
            {customer?.chat_type !== 'support' && (
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (₹)</label>
                <input type="number" value={srAmount} onChange={(e) => setSrAmount(e.target.value)}
                       placeholder="e.g. 2000"
                       className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
            )}
            {/* P23 — Deposit: UTR (required) + optional received-in */}
            {customer?.chat_type === 'deposit' && (
              <>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">UTR Number (required)</label>
                  <input type="text" value={srUtr} onChange={(e) => setSrUtr(e.target.value)}
                         placeholder="6–22 letters/digits"
                         className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                         data-testid="handler-sr-utr-input" />
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Received In (optional)</label>
                  <input type="text" value={srReceivedIn} onChange={(e) => setSrReceivedIn(e.target.value)}
                         placeholder="e.g. HDFC ****1234 / UPI"
                         className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                         data-testid="handler-sr-receivedin-input" />
                </div>
              </>
            )}
            {/* P24 — Withdraw: prefilled editable bank details */}
            {customer?.chat_type === 'withdrawal' && (
              <div className="mb-3 space-y-2 border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold text-slate-500">Bank details (prefilled — editable)</p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const raw = await navigator.clipboard.readText();
                        if (!raw || !raw.trim()) { toast.error('Clipboard is empty'); return; }
                        const parsed = parseBankFromPaste(raw);
                        const keys = Object.keys(parsed);
                        if (keys.length === 0) { toast.error('No bank details found in clipboard'); return; }
                        setSrBank(b => {
                          const next = { ...b };
                          for (const k of keys) if (parsed[k]) next[k] = parsed[k];
                          return next;
                        });
                        toast.success(`Filled ${keys.length} field${keys.length === 1 ? '' : 's'} from clipboard`);
                      } catch (err) {
                        toast.error('Clipboard access denied — allow permission and try again');
                      }
                    }}
                    className="px-2 py-0.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm flex items-center gap-1 flex-shrink-0"
                    data-testid="handler-sr-bank-paste-btn"
                    title="Paste a copied withdrawal form — auto-fills the fields"
                  >
                    <span>📋</span> Paste
                  </button>
                </div>
                {srBankAccounts.length > 1 && (
                  <select
                    value={srBankPickId}
                    onChange={(e) => {
                      const acc = srBankAccounts.find(a => a.id === e.target.value);
                      setSrBankPickId(e.target.value);
                      if (acc) {
                        const v = acc.value;
                        setSrBank(b => ({ ...b, account_holder_name: v.account_holder_name || '', bank_account_number: v.bank_account_number || '', ifsc_code: v.ifsc_code || '', bank_name: v.bank_name || '' }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm bg-white font-medium"
                    data-testid="handler-sr-bank-picker"
                  >
                    {srBankAccounts.map(a => (
                      <option key={a.id} value={a.id}>
                        {(a.value.account_holder_name || 'Account')} · {a.value.bank_name || ''} ····{String(a.value.bank_account_number || '').slice(-4)}{a.is_active ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <input type="text" value={srBank.account_holder_name} onChange={(e) => setSrBank(b => ({ ...b, account_holder_name: e.target.value }))} placeholder="Account holder name" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="handler-sr-bank-name-input" />
                <input type="text" value={srBank.bank_account_number} onChange={(e) => setSrBank(b => ({ ...b, bank_account_number: e.target.value }))} placeholder="Account number" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" data-testid="handler-sr-bank-acc-input" />
                <div className="flex gap-2">
                  <input type="text" value={srBank.ifsc_code} onChange={(e) => setSrBank(b => ({ ...b, ifsc_code: e.target.value.toUpperCase() }))} placeholder="IFSC" className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono uppercase" data-testid="handler-sr-bank-ifsc-input" />
                  <input type="text" value={srBank.bank_name} onChange={(e) => setSrBank(b => ({ ...b, bank_name: e.target.value }))} placeholder="Bank name" className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="handler-sr-bank-bankname-input" />
                </div>
                <input type="text" value={srBank.upi_id} onChange={(e) => setSrBank(b => ({ ...b, upi_id: e.target.value }))} placeholder="UPI ID (optional)" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="handler-sr-bank-upi-input" />
              </div>
            )}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {customer?.chat_type === 'support' ? 'Query / Message (required)' : 'Reference (optional)'}
              </label>
              <textarea value={srReference} onChange={(e) => setSrReference(e.target.value)}
                        rows={2} placeholder="Optional note"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
            <div className="flex gap-2 justify-end">
              <button disabled={srBusy} onClick={() => { setShowSRCreateModal(false); setSrAmount(''); setSrReference(''); setSrUtr(''); setSrReceivedIn(''); }}
                      className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
              <button disabled={srBusy}
                      onClick={async () => {
                        const ct = customer?.chat_type;
                        if (ct === 'support' && !srReference.trim()) { toast.error('Enter the query'); return; }
                        if (ct !== 'support' && !srAmount) { toast.error('Enter the amount'); return; }
                        if (ct === 'deposit') {
                          const utr = srUtr.trim();
                          if (!/^[A-Za-z0-9]{6,22}$/.test(utr)) { toast.error('Enter a valid UTR (6–22 chars)'); return; }
                        }
                        setSrBusy(true);
                        const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
                          ? crypto.randomUUID()
                          : `cmid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
                        const optimisticCard = {
                          id: `optimistic_sr_${clientMessageId}`,
                          client_message_id: clientMessageId,
                          pending: true,
                          room_id: roomId,
                          sender_id: user.id,
                          sender_role: user.role,
                          sender_name: user.username || 'You',
                          text: ct === 'support'
                            ? `📋 Support Request\n${srReference.trim()}`
                            : `📋 ${ct === 'withdrawal' ? 'Withdraw' : 'Deposit'} Request${srAmount ? `\nAmount: ₹${srAmount}` : ''}${srReference.trim() ? `\nRef: ${srReference.trim()}` : ''}`,
                          timestamp: new Date().toISOString(),
                          read_status: 'sending',
                        };
                        setMessages(prev => [...prev, optimisticCard]);
                        setIsUserScrolling(false);
                        try {
                          const backendType = ct === 'withdrawal' ? 'withdraw' : ct;
                          // Step 1 — create the request (ticket-created message)
                          await axios.post(`${API}/service-requests/create`, {
                              type: backendType, room_id: roomId, actor_id: user.id,
                              on_behalf_customer_id: customer?.id,
                              amount: srAmount ? Number(srAmount) : undefined,
                              reference: srReference.trim() || undefined,
                              client_message_id: clientMessageId,
                            });
                          // Step 2 — immediately approve (done / coins-cut message)
                          if (ct === 'deposit') {
                            await axios.post(`${API}/deposit-done/notify`, {
                              actor_id: user.id, customer_id: customer.id, amount: String(srAmount),
                              room_id: roomId, utr_number: srUtr.trim(), received_in: srReceivedIn.trim(),
                              received_from: '', description: '', bank_account: srReceivedIn.trim(),
                            });
                            toast.success('✅ Request created & deposit approved');
                          } else if (ct === 'withdrawal') {
                            await axios.post(`${API}/coin-cut/broadcast`, {
                              actor_id: user.id, customer_id: customer.id, amount: String(srAmount),
                              room_id: roomId, reference: srReference.trim() || null,
                              bank_entry_id: srBankPickId || undefined,
                              bank_override: {
                                account_holder_name: srBank.account_holder_name.trim(),
                                bank_account_number: srBank.bank_account_number.trim(),
                                ifsc_code: srBank.ifsc_code.trim(),
                                bank_name: srBank.bank_name.trim(),
                                upi_id: srBank.upi_id.trim(),
                              },
                            });
                            toast.success('✅ Request created & coin cut done');
                          } else {
                            toast.success('✅ Request created');
                          }
                          setShowSRCreateModal(false);
                          setSrAmount(''); setSrReference(''); setSrUtr(''); setSrReceivedIn('');
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Failed');
                          setMessages(prev => prev.map(m =>
                            m.id === optimisticCard.id
                              ? { ...m, pending: false, send_failed: true, read_status: 'failed' }
                              : m
                          ));
                        } finally { setSrBusy(false); }
                      }}
                      className="px-4 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                      data-testid="handler-sr-create-submit">
                {srBusy ? 'Processing…' : customer?.chat_type === 'deposit' ? 'Create & Approve' : customer?.chat_type === 'withdrawal' ? 'Approve & Coin Cut' : 'Create Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session-40: Handler-side Cancel Request modal */}
      {showSRCancelModal && (
        <DraggableModal
          open={showSRCancelModal}
          onClose={() => { if (!srBusy) { setShowSRCancelModal(false); setSrReasonForId(null); setSrReasonText(''); } }}
          testId="handler-sr-cancel-modal"
          title="❌ Cancel Open Requests"
        >
          <div>
            <p className="text-[11px] text-slate-500 mb-2">Select a request to cancel for <b>{customer?.name || customer?.username}</b></p>
            <div className="space-y-2 mb-3">
              {srCancelList.map(req => (
                <div key={req.id}
                     className="border border-slate-200 rounded-md p-2.5 bg-slate-50"
                     data-testid={`handler-sr-cancel-item-${req.request_no}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="font-bold text-slate-800 text-sm">{req.request_no}</div>
                    <div className="text-[11px] text-slate-500">
                      {req.amount ? `₹${req.amount}` : ''} {req.reference ? `· ${req.reference}` : ''}
                    </div>
                  </div>
                  {srReasonForId === req.id ? (
                    <>
                      <textarea
                        value={srReasonText}
                        onChange={(e) => setSrReasonText(e.target.value)}
                        placeholder="Reason"
                        rows={2}
                        autoFocus
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs resize-none focus:outline-none focus:border-red-400"
                        data-testid={`handler-sr-cancel-reason-${req.request_no}`}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => { setSrReasonForId(null); setSrReasonText(''); }}
                          disabled={srBusy}
                          className="flex-1 py-1.5 border border-slate-300 rounded text-xs">Back</button>
                        <button
                          disabled={srBusy || !srReasonText.trim()}
                          onClick={async () => {
                            setSrBusy(true);
                            try {
                              await axios.post(`${API}/service-requests/cancel`, {
                                  request_id: req.id, actor_id: user.id, reason: srReasonText.trim(),
                                });
                              setSrCancelList(prev => prev.filter(r => r.id !== req.id));
                              setSrReasonForId(null); setSrReasonText('');
                              toast.success(`❌ ${req.request_no} cancelled`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Failed');
                            } finally { setSrBusy(false); }
                          }}
                          className="flex-1 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-50"
                          data-testid={`handler-sr-cancel-submit-${req.request_no}`}
                        >{srBusy ? 'Cancelling…' : 'Confirm Cancel'}</button>
                      </div>
                    </>
                  ) : (
                    <button
                      disabled={srBusy}
                      onClick={() => { setSrReasonForId(req.id); setSrReasonText(''); }}
                      className="w-full py-1 rounded bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold border border-red-200"
                    >Cancel this request →</button>
                  )}
                </div>
              ))}
              {srCancelList.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-4">All cancelled.</div>
              )}
            </div>
            <div className="flex justify-end">
              <button onClick={() => { setShowSRCancelModal(false); setSrReasonForId(null); setSrReasonText(''); }}
                      className="px-3 py-1.5 text-sm rounded border border-slate-300">Close</button>
            </div>
          </div>
        </DraggableModal>
      )}
      {/* Session-48j — Mark-as-Solved note modal */}
      <MarkSolvedNoteModal
        open={markSolvedOpen}
        busy={markSolvedBusy}
        onClose={() => setMarkSolvedOpen(false)}
        onConfirm={doMarkSolved}
      />
    </div>
  );
};

export default HandlerChat;
